import { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  Settings,
  Mail,
  Sparkles,
  Save,
  Loader2,
  Check,
  AlertCircle,
  FileText,
  Plus,
  Download,
  Trash2,
  Edit3,
  X,
  Store,
  Tag,
  Zap,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ChevronRight,
  RotateCcw,
  RefreshCw,
  Bot,
  Eye,
  Users,
  Shield,
  PowerOff,
  Play,
  Lock,
  Unlock,
  CheckCircle2,
  GitBranch,
  BarChart2,
  Star,
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { aiApi, messagesApi, storeApi, inboxEmailApi, userApi } from '../lib/api';
import type { MessageTemplateCategoryDto } from '../lib/api';
import { withPlainTextPaste } from '../lib/quillPlainTextPaste';
import type { InboxSwipeAction } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { BackLink } from '../components/ui/BackLink';
import { SettingsSection } from '../components/ui/SettingsSection';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { CategoryAutoActionRow } from '../components/ui/CategoryAutoActionRow';
import { ToggleRow } from '../components/ui/ToggleRow';
import { Alert } from '../components/ui/Alert';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SettingsSaveBar } from '../components/SettingsSaveBar';
import { MessageTemplateVariablePicker } from '../components/messages/MessageTemplateVariablePicker';

interface AiProvider {
  id: string;
  name: string;
  displayName: string;
  modelId: string;
  isActive: boolean;
}

interface AiSettings {
  preferredProvider: string;
  autoReply: boolean;
  aiTemplatePersonalization?: boolean;
  systemPrompt?: string;
}

interface StoreItem {
  id: string;
  name: string;
}

interface MessageTemplate {
  id: string;
  name: string;
  subject: string | null;
  bodyHtml: string;
  bodyText: string | null;
  storeId: string | null;
  categoryId: string | null;
  isActive: boolean;
  position: number;
  store?: { id: string; name: string } | null;
  category?: { id: string; name: string; position: number } | null;
  shortcut?: string | null;
  autoSend?: boolean;
}

interface InboxLabel {
  id: string;
  name: string;
  color: string;
  position: number;
  _count?: { messages: number };
}

interface ImapConfig {
  id: string;
  name: string;
  host: string;
  fromEmail: string;
  isActive: boolean;
  lastSyncAt: string | null;
  aiEnabledFrom: string | null;
  signatureHtml: string | null;
  signatureText: string | null;
  signatureMarginTop?: number;
  store?: { id: string; name: string } | null;
}

const DEFAULT_SYSTEM_PROMPT = `Jesteś AI obsługującym wiadomości klientów sklepu e-commerce. Tworzysz gotowe odpowiedzi — kompletne, do wysłania bez edycji.

DOSTĘPNE DANE (mogą pojawić się w wiadomości):
- Baza wiedzy o produktach i politykach sklepu (dołączona poniżej, jeśli istnieje)
- Imię/email klienta
- Ostatnie zamówienia klienta (jeśli znalezione w systemie)
- Historia wątku (jeśli to kontynuacja konwersacji)
- Wskazówka operatora (jeśli podana)

---

LOGIKA DECYZYJNA — czytaj w tej kolejności:

1. Jeśli operator podał wskazówkę → traktuj ją jako priorytetową instrukcję. Zbuduj odpowiedź zgodnie z nią.

2. Jeśli klient pyta o status zamówienia / dostawę / tracking:
   → Znajdź zamówienie w danych zamówień (po numerze lub kontekście)
   → Podaj konkretny status, numer tracking, przewidywany termin
   → Jeśli zamówienie nie pasuje do żadnego z dostępnych → napisz, że sprawdzisz i wrócisz z informacją

3. Jeśli klient pyta o produkt (cechy, dostępność, różnice, zastosowanie):
   → Szukaj odpowiedzi najpierw w bazie wiedzy
   → Jeśli baza wiedzy nie zawiera odpowiedzi — użyj swojej wiedzy, odpowiadaj pewnie i kompetentnie, jak doświadczony pracownik znający asortyment
   → NIE zaznaczaj skąd masz wiedzę, nie używaj zwrotów typu "ogólnie", "z tego co wiem", "tego typu produkty zazwyczaj"
   → Dopiero jeśli naprawdę nie wiesz — przekaż pytanie do specjalisty
   → NIE zgaduj i NIE konfabuluj. Jeśli nie jesteś pewien — eskaluj. Lepsza eskalacja niż błędna informacja

4. Jeśli klient zgłasza reklamację / problem / zwrot:
   → Potwierdź przyjęcie zgłoszenia
   → Odnieś się do danych zamówienia, jeśli klient podał numer
   → Opisz następne kroki (zgodnie z bazą wiedzy, jeśli zawiera politykę zwrotów/reklamacji)
   → NIE obiecuj konkretnych rozwiązań (wymiana, zwrot pieniędzy), których nie masz potwierdzonych

5. Jeśli wiadomość to kontynuacja wątku:
   → Przeczytaj historię wątku
   → NIE powtarzaj informacji już podanych
   → Nawiąż do wcześniejszych ustaleń
   → Jeśli klient odpowiada na konkretne pytanie z poprzedniej wiadomości — odnieś się do tego pytania

6. Jeśli nie możesz odpowiedzieć (brak danych, nietypowa sprawa):
   → Napisz, że przekazujesz sprawę do obsługi i klient otrzyma odpowiedź
   → NIE wymyślaj odpowiedzi

---

ZASADY TWORZENIA ODPOWIEDZI:

Język: odpowiadaj w tym samym języku, w którym pisze klient.

Ton: uprzejmy, rzeczowy, bez nadmiernej formalności. Zwracaj się per "Pan/Pani" chyba że klient pisze na "ty" — wtedy dostosuj się.

Format:
- Zacznij od odniesienia do sprawy klienta (nie od "Szanowny Kliencie" ani "Dziękujemy za kontakt")
- Jeśli jest kilka punktów — użyj numerowanej listy
- Możesz zakończyć krótkim zdaniem zamykającym (np. "W razie pytań, proszę śmiało pisać" lub "Daj znać, jeśli mogę jeszcze pomóc")
- Max 150 słów, chyba że sprawa wymaga dłuższego wyjaśnienia

ZAKAZY:
- NIE wymyślaj numerów zamówień, dat dostawy, cen, stanów magazynowych
- NIE wymyślaj linków ani adresów URL
- NIE obiecuj rzeczy, których nie wiesz (termin rozpatrzenia, konkretna rekompensata)
- NIE przepraszaj więcej niż raz w odpowiedzi
- NIE używaj frazesów: "z przyjemnością", "jest mi niezmiernie miło", "nie omieszkam"
- NIE zaczynaj od "Dzień dobry" jeśli historia wątku wskazuje, że rozmowa trwa
- NIE powtarzaj informacji z poprzednich wiadomości w wątku
- NIE dodawaj informacji, o które klient nie pytał
- NIE dodawaj stopki, podpisu ani formułki zamykającej typu "Pozdrawiamy", "Z poważaniem", "Zespół obsługi klienta" — stopka jest dodawana automatycznie przez system. Twoja odpowiedź kończy się na merytorycznej treści lub krótkim zdaniu zamykającym, nie na podpisie.`;

// ─── Sekcje Ustawień Wiadomości (overview grid — wzorzec Logistyki) ─────
// Cztery tematyczne kategorie. Opisy narracyjne (~90 znaków, zbliżonej
// długości), bez wyliczania pojedynczych opcji — bo lista opcji rośnie
// z czasem i konkretne wypunktowanie szybko by się dezaktualizowało.
// #R7-6 - ujednolicony jeden kolor ikon (text-accent-primary + bg-accent-primary/10)
// dla wszystkich sekcji ustawień Wiadomości. Wcześniej każda sekcja miała inny
// accent (warning, tertiary, secondary, secondary-text dla "Ogólne") - user
// zgłosił że to wygląda chaotycznie, bo niektóre sekcje wyróżniały się kolorem
// jak wyjątek. Spójny pattern z ustawień Logistyki.
const MESSAGES_SETTINGS_SECTIONS = [
  {
    slug: 'general',
    title: 'Ogólne',
    description:
      'Podstawowe preferencje interfejsu: plakietki, statusy konwersacji, gesty swipe.',
    icon: Settings,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    slug: 'ai',
    title: 'AI i automatyzacje',
    description:
      'Konfiguracja Twoich asystentów AI, którzy będą wspierać Cię w obsłudze Klienta.',
    icon: Sparkles,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    slug: 'automation',
    title: 'Reguły i skrzynki',
    description:
      'Ustawienia automatycznych działań na wiadomościach i ogólna konfiguracja.',
    icon: Zap,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    slug: 'content',
    title: 'Szablony i treść',
    description:
      'Wszystko co kształtuje wygląd i organizację Twoich odpowiedzi oraz wątków w skrzynce.',
    icon: FileText,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
  {
    slug: 'rodo',
    title: 'RODO i retencja',
    description:
      'Zasady przechowywania treści wiadomości zgodnie z RODO oraz dziennik operacji na danych.',
    icon: Shield,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary/10',
  },
] as const;

export function MessagesSettingsPage() {
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // System prompt lock state
  const [promptEditable, setPromptEditable] = useState(false);
  const [showPromptWarning, setShowPromptWarning] = useState(false);
  // Modal edytora promptu — zajmował za dużo miejsca jako inline textarea.
  const [showPromptModal, setShowPromptModal] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [templateCategories, setTemplateCategories] = useState<MessageTemplateCategoryDto[]>([]);
  const [activeTemplateCategoryId, setActiveTemplateCategoryId] = useState<'all' | 'uncategorized' | string>('all');
  const [showTemplateCategoryForm, setShowTemplateCategoryForm] = useState(false);
  const [templateCategoryName, setTemplateCategoryName] = useState('');
  const [savingTemplateCategory, setSavingTemplateCategory] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<MessageTemplateCategoryDto | null>(null);
  const [stores, setStores] = useState<StoreItem[]>([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    bodyHtml: '',
    storeId: '' as string | null,
    categoryId: '' as string | null,
    isActive: true,
    shortcut: '' as string | null,
    autoSend: false,
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const templateEditorRef = useRef<ReactQuill | null>(null);
  const templateSelectionRef = useRef<{ index: number; length: number } | null>(null);

  // System templates load (opt-in import biblioteki SUPER_ADMIN)
  const [showLoadSystemDialog, setShowLoadSystemDialog] = useState(false);
  const [systemTemplatesCount, setSystemTemplatesCount] = useState<number | null>(null);
  const [loadingSystemCount, setLoadingSystemCount] = useState(false);
  const [importingSystem, setImportingSystem] = useState(false);

  const visibleTemplates = useMemo(() => {
    if (activeTemplateCategoryId === 'all') return templates;
    if (activeTemplateCategoryId === 'uncategorized') {
      return templates.filter(template => !template.categoryId);
    }
    return templates.filter(template => template.categoryId === activeTemplateCategoryId);
  }, [activeTemplateCategoryId, templates]);

  // Labels state
  const [labelsList, setLabelsList] = useState<InboxLabel[]>([]);
  const [showLabelForm, setShowLabelForm] = useState(false);
  const [editingLabel, setEditingLabel] = useState<InboxLabel | null>(null);
  const [labelForm, setLabelForm] = useState({ name: '', color: '#6B7280' });

  // IMAP configs state (nadal potrzebne przy wyświetlaniu listy kont w innych miejscach,
  // np. kontekst reguł). Foldery (custom i status-mapping) oraz deep-sync usunięte
  // po migracji do zero-persistence — zarządzanie folderami przeniesione do InboxSidebar.
  const [imapConfigs, setImapConfigs] = useState<ImapConfig[]>([]);

  // Status mapping state
  interface FolderOption { id: string; path: string; name: string }
  const [statusMapping, setStatusMapping] = useState<{ pending: Record<string, string>; important: Record<string, string>; resolved: Record<string, string>; starred: Record<string, string> }>({ pending: {}, important: {}, resolved: {}, starred: {} });
  const [imapFolderOptions, setImapFolderOptions] = useState<Record<string, FolderOption[]>>({});
  const [refreshingImapFolders, setRefreshingImapFolders] = useState(false);

  // Rules state
  interface RuleCondition { field: string; operator: string; value: string }
  interface RuleAction { type: string; config: Record<string, string> }
  interface InboxRuleItem {
    id: string;
    name: string;
    storeId: string | null;
    store?: { id: string; name: string } | null;
    conditions: RuleCondition[];
    conditionLogic: 'all' | 'any';
    actions: RuleAction[];
    isActive: boolean;
    priority: number;
    position: number;
    stopProcessing: boolean;
    matchCount: number;
    lastMatchAt: string | null;
  }

  const [rulesList, setRulesList] = useState<InboxRuleItem[]>([]);
  const [applyingRuleId, setApplyingRuleId] = useState<string | null>(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState<InboxRuleItem | null>(null);
  const [ruleForm, setRuleForm] = useState<{
    name: string;
    storeId: string | null;
    conditions: RuleCondition[];
    conditionLogic: 'all' | 'any';
    actions: RuleAction[];
    isActive: boolean;
    stopProcessing: boolean;
  }>({
    name: '',
    storeId: null,
    conditions: [{ field: 'subject', operator: 'contains', value: '' }],
    conditionLogic: 'all',
    actions: [{ type: 'mark_as_read', config: {} }],
    isActive: true,
    stopProcessing: false,
  });

  // AI Category Config state
  interface CategoryConfig {
    id: string;
    category: string;
    priority: number;
    autoAction: string | null;
    templateId: string | null;
    isActive: boolean;
  }
  const [categoryConfigs, setCategoryConfigs] = useState<CategoryConfig[]>([]);
  const [savingCategory, setSavingCategory] = useState<string | null>(null);

  // Auto-Reply Config state
  interface AutoReplyConfigData {
    id: string;
    isEnabled: boolean;
    globalMinConfidence: number;
    reviewMinConfidence: number;
    maxDailyAutoReplies: number;
    reviewQueueEnabled: boolean;
    categoryRules: Record<string, { enabled?: boolean; minConfidence?: number; autoSend?: boolean }>;
    aiPermissionLevel: number;
    allowCategorization: boolean;
    allowAutoActions: boolean;
    allowDraftGeneration: boolean;
    allowAutoSend: boolean;
    draftsOnlyForInbox: boolean;
    autoResolveOnSend: boolean;
    deleteDraftOnMessageGone: boolean;
    draftsTargetFolder: Record<string, string>;
  }
  const [autoReplyConfig, setAutoReplyConfig] = useState<AutoReplyConfigData | null>(null);
  const [savingAutoReply, setSavingAutoReply] = useState(false);

  // RODO — audit log purge (read-only)
  interface PurgeLogRow {
    id: string;
    tenantId: string;
    triggeredBy: string | null;
    scope: string;
    scopeId: string | null;
    bodiesDeleted: number;
    snippetsCleared: number;
    olderThanDays: number | null;
    createdAt: string;
  }
  const [purgeLogs, setPurgeLogs] = useState<PurgeLogRow[]>([]);
  const [purgeLogsLoading, setPurgeLogsLoading] = useState(false);
  const [purgeLogsOpen, setPurgeLogsOpen] = useState(false);
  const [purgeLogsTotal, setPurgeLogsTotal] = useState(0);

  // RODO disclosure popover — domyślnie ukryty, toggle ikonką "?"
  const [showRodoPopover, setShowRodoPopover] = useState(false);

  // RODO — retencja treści maili (inbox.emailBodyRetentionDays)
  const [inboxRetentionDays, setInboxRetentionDays] = useState<number>(365);
  const [inboxRetentionLoaded, setInboxRetentionLoaded] = useState(false);
  // Domyślna skrzynka (storeId) — operator startuje zawsze w tym sklepie.
  // null = brak preferencji (frontend weźmie pierwszy aktywny sklep).
  const [defaultStoreId, setDefaultStoreId] = useState<string | null>(null);

  // Plakietki (liczniki nieprzeczytanych) per grupa sidebara — user decyduje
  // czy chce widzieć je przy Odebranych / Folderach IMAP / Ulubionych.
  const queryClient = useQueryClient();
  const [badgeDisplay, setBadgeDisplay] = useState<{ navigation: boolean; inbox: boolean; imapFolders: boolean; favorites: boolean }>({
    navigation: true,
    inbox: true,
    imapFolders: true,
    favorites: true,
  });

  // Swipe gesty w widoku listy maili — per-user preference (nie tenant-wide).
  // Default OFF żeby nie zmieniać zachowania istniejącym userom; nowi włączają
  // świadomie. Status Rozwiązane działa również bez mapowania folderu IMAP.
  const [swipeEnabled, setSwipeEnabled] = useState(false);
  const [swipeLeftAction, setSwipeLeftAction] = useState<InboxSwipeAction>('trash');
  const [swipeRightAction, setSwipeRightAction] = useState<InboxSwipeAction>('resolved');

  // AI per-skrzynka toggle state (per-config, keyed by config.id)
  const [savingAiConfig, setSavingAiConfig] = useState<string | null>(null);
  // Stopka per-skrzynka — draft state per config id (użytkownik edytuje, potem Zapisz).
  const [signatureDrafts, setSignatureDrafts] = useState<Record<string, { html: string; text: string; marginTop: number }>>({});
  // showAdvancedAi removed — advanced toggles replaced by level bullet points
  interface AutoReplyStatsData {
    todayAutoSent: number;
    todayApproved: number;
    todayRejected: number;
    totalPending: number;
    todayTotal: number;
  }
  const [autoReplyStats, setAutoReplyStats] = useState<AutoReplyStatsData | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadImapFolderOptions = async (configs: ImapConfig[]) => {
    const folderTrees = await Promise.all(
      Array.from(new Set(configs.map(config => config.store?.id).filter((id): id is string => Boolean(id))))
        .map(storeId => inboxEmailApi.getFoldersTree(storeId).catch(() => ({ data: { data: [] } }))),
    );
    const tree = folderTrees.flatMap(result => result.data?.data || []);
    const optionsMap: Record<string, FolderOption[]> = {};
    for (const account of (Array.isArray(tree) ? tree : [])) {
      const flatFolders: FolderOption[] = [];
      const flatten = (folders: any[]) => {
        for (const folder of folders) {
          flatFolders.push({ id: folder.id, path: folder.path, name: folder.path });
          if (folder.children?.length) flatten(folder.children);
        }
      };
      if (account.folders) flatten(account.folders);
      optionsMap[account.id] = flatFolders;
    }
    setImapFolderOptions(optionsMap);
  };

  const refreshImapFolders = async () => {
    if (refreshingImapFolders) return;
    setRefreshingImapFolders(true);
    try {
      const results = await Promise.allSettled(imapConfigs.map(config => inboxEmailApi.discoverFolders(config.id)));
      const failed = results.filter(result => result.status === 'rejected').length;
      await loadImapFolderOptions(imapConfigs);
      if (failed > 0) {
        showToast(`Odświeżono foldery, ale ${failed} ${failed === 1 ? 'skrzynka nie odpowiedziała' : 'skrzynki nie odpowiedziały'}.`, 'error');
      } else {
        showToast('Lista folderów została odświeżona.');
      }
    } catch {
      showToast('Nie udało się odświeżyć folderów.', 'error');
    } finally {
      setRefreshingImapFolders(false);
    }
  };

  // Quill config
  const quillModules = useMemo(() => withPlainTextPaste({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      ['clean'],
    ],
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link',
  ];

  // ESC closes modals
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showPromptWarning) setShowPromptWarning(false);
        else if (showPromptModal) setShowPromptModal(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [showPromptWarning, showPromptModal]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersRes, settingsRes, templatesRes, templateCategoriesRes, storesRes, labelsRes, rulesRes, imapRes, catConfigRes, autoReplyConfigRes, autoReplyStatsRes, statusMappingRes, tenantSettingsRes, userPrefsRes] = await Promise.all([
        aiApi.getProviders(),
        aiApi.getSettings(),
        messagesApi.getAllTemplates(),
        messagesApi.getTemplateCategories(),
        storeApi.getAll(),
        messagesApi.getLabels(),
        messagesApi.getRules(),
        inboxEmailApi.getConfigs(),
        messagesApi.getCategoryConfigs().catch(() => ({ data: { data: [] } })),
        messagesApi.getAutoReplyConfig().catch(() => ({ data: { data: null } })),
        messagesApi.getAutoReplyStats().catch(() => ({ data: { data: null } })),
        messagesApi.getStatusMapping().catch(() => ({ data: {} })),
        messagesApi.getTenantSettings().catch(() => ({ data: null })),
        userApi.getPreferences().catch(() => ({ data: {} })),
      ]);
      setProviders(providersRes.data.data);
      const loadedSettings = settingsRes.data.data;
      // Fix: jeśli user ma stary domyślny prompt (identyczny z obecnym) lub pusty → użyj DEFAULT_SYSTEM_PROMPT
      if (
        !loadedSettings.systemPrompt ||
        (loadedSettings.systemPrompt as string).startsWith('Jesteś AI obsługującym wiadomości klientów sklepu e-commerce.')
      ) {
        loadedSettings.systemPrompt = DEFAULT_SYSTEM_PROMPT;
      }
      setSettings(loadedSettings);
      setTemplates(templatesRes.data.data || []);
      setTemplateCategories(templateCategoriesRes.data.data || []);
      const storeList = storesRes.data;
      setStores(Array.isArray(storeList) ? storeList : (storeList as any).data || []);
      setLabelsList(labelsRes.data.data || []);
      setRulesList(rulesRes.data.data || []);
      const activeCfgs: ImapConfig[] = (imapRes.data.data || []).filter((c: ImapConfig) => c.isActive);
      setImapConfigs(activeCfgs);
      // Hydratacja draftów stopki per-skrzynka — pozwala edytować bez utraty
      // istniejącej wartości, a savedText/Html jest widoczne od razu po otwarciu.
      setSignatureDrafts(Object.fromEntries(
        activeCfgs.map(c => [c.id, { html: c.signatureHtml ?? '', text: c.signatureText ?? '', marginTop: c.signatureMarginTop ?? 10 }]),
      ));
      setCategoryConfigs(catConfigRes.data.data || []);
      const tenantSettings = tenantSettingsRes.data as { inbox?: { emailBodyRetentionDays?: number; defaultStoreId?: string | null; badgeDisplay?: { navigation?: boolean; inbox?: boolean; imapFolders?: boolean; favorites?: boolean } } } | null;
      if (tenantSettings?.inbox?.emailBodyRetentionDays != null) {
        setInboxRetentionDays(tenantSettings.inbox.emailBodyRetentionDays);
      }
      setDefaultStoreId(tenantSettings?.inbox?.defaultStoreId ?? null);
      if (tenantSettings?.inbox?.badgeDisplay) {
        setBadgeDisplay({
          navigation: tenantSettings.inbox.badgeDisplay.navigation ?? true,
          inbox: tenantSettings.inbox.badgeDisplay.inbox ?? true,
          imapFolders: tenantSettings.inbox.badgeDisplay.imapFolders ?? true,
          favorites: tenantSettings.inbox.badgeDisplay.favorites ?? true,
        });
      }
      const userPrefs = (userPrefsRes.data || {}) as Record<string, unknown>;
      if (typeof userPrefs.inboxSwipeEnabled === 'boolean') {
        setSwipeEnabled(userPrefs.inboxSwipeEnabled);
      }
      if (typeof userPrefs.inboxSwipeLeftAction === 'string') {
        const action = userPrefs.inboxSwipeLeftAction as InboxSwipeAction;
        setSwipeLeftAction(['none', 'resolved', 'drafts', 'archive', 'trash'].includes(action) ? action : 'trash');
      }
      if (typeof userPrefs.inboxSwipeRightAction === 'string') {
        const action = userPrefs.inboxSwipeRightAction as InboxSwipeAction;
        setSwipeRightAction(['none', 'resolved', 'drafts', 'archive', 'trash'].includes(action) ? action : 'resolved');
      }
      setInboxRetentionLoaded(true);
      if (autoReplyConfigRes.data.data) {
        const cfg = autoReplyConfigRes.data.data;
        // Backfill default: pre-existing tenants mają brak pola → domyślnie ON
        setAutoReplyConfig({
          ...cfg,
          draftsOnlyForInbox: cfg.draftsOnlyForInbox ?? true,
          autoResolveOnSend: cfg.autoResolveOnSend ?? false,
          deleteDraftOnMessageGone: cfg.deleteDraftOnMessageGone ?? true,
          draftsTargetFolder: (cfg.draftsTargetFolder as Record<string, string>) ?? {},
        });
      }
      if (autoReplyStatsRes.data.data) setAutoReplyStats(autoReplyStatsRes.data.data);
      // Status mapping
      const mappingData = statusMappingRes.data || {};
      setStatusMapping({
        pending: {},
        important: {},
        resolved: (mappingData as any).resolved || {},
        starred: (mappingData as any).starred || {},
      });
      // Foldery pobieramy osobno per sklep, żeby cache i liczniki nie mieszały
      // połączeń kilku marek tenanta.
      await loadImapFolderOptions(activeCfgs);
    } catch {
      showToast('Błąd ładowania ustawień', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveAllAiSettings = async () => {
    try {
      setSaving(true);
      const aiPromise = settings ? aiApi.updateSettings(settings) : Promise.resolve(null);
      const autoReplyPromise = autoReplyConfig
        ? messagesApi.updateAutoReplyConfig({
            isEnabled: autoReplyConfig.isEnabled,
            globalMinConfidence: autoReplyConfig.globalMinConfidence,
            reviewMinConfidence: autoReplyConfig.reviewMinConfidence,
            maxDailyAutoReplies: autoReplyConfig.maxDailyAutoReplies,
            reviewQueueEnabled: autoReplyConfig.reviewQueueEnabled,
            categoryRules: autoReplyConfig.categoryRules,
            aiPermissionLevel: autoReplyConfig.aiPermissionLevel,
            allowCategorization: autoReplyConfig.allowCategorization,
            allowAutoActions: autoReplyConfig.allowAutoActions,
            allowDraftGeneration: autoReplyConfig.allowDraftGeneration,
            allowAutoSend: autoReplyConfig.allowAutoSend,
            draftsOnlyForInbox: autoReplyConfig.draftsOnlyForInbox,
            autoResolveOnSend: autoReplyConfig.autoResolveOnSend,
            deleteDraftOnMessageGone: autoReplyConfig.deleteDraftOnMessageGone,
            draftsTargetFolder: autoReplyConfig.draftsTargetFolder,
          })
        : Promise.resolve(null);
      const retentionPromise = inboxRetentionLoaded
        ? messagesApi.updateInboxSettings({ emailBodyRetentionDays: inboxRetentionDays })
        : Promise.resolve(null);
      const [, autoReplyRes] = await Promise.all([aiPromise, autoReplyPromise, retentionPromise]);

      // Jeśli właśnie włączono deleteDraftOnMessageGone, backend zwraca ile
      // orphaned draftów wyczyszczono (retroaktywnie dla maili już poza Inboxem).
      const cleaned = (autoReplyRes as { data?: { orphanedCleanedUp?: number } } | null)?.data?.orphanedCleanedUp ?? 0;
      if (cleaned > 0) {
        showToast(`Wyczyszczono ${cleaned} ${cleaned === 1 ? 'starego drafta' : 'starych draftów'} (maile już poza Inboxem)`);
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  const finishSettingsSave = (status: 'success' | 'error') => {
    setSaveStatus(status);
    setTimeout(() => setSaveStatus('idle'), 3000);
  };

  const saveGeneralSettings = async () => {
    try {
      setSaving(true);
      const visibleStatusMapping = {
        pending: {},
        important: {},
        resolved: statusMapping.resolved,
        starred: statusMapping.starred,
      };
      await Promise.all([
        messagesApi.updateStatusMapping(visibleStatusMapping),
        messagesApi.updateInboxSettings({ badgeDisplay }),
        userApi.updatePreferences({
          inboxSwipeEnabled: swipeEnabled,
          inboxSwipeLeftAction: swipeLeftAction,
          inboxSwipeRightAction: swipeRightAction,
        }),
      ]);
      setStatusMapping(visibleStatusMapping);
      void queryClient.invalidateQueries({ queryKey: ['navigation-messages'] });
      finishSettingsSave('success');
    } catch {
      finishSettingsSave('error');
    } finally {
      setSaving(false);
    }
  };

  const saveAutomationSettings = async () => {
    try {
      setSaving(true);
      await messagesApi.updateInboxSettings({ defaultStoreId });
      finishSettingsSave('success');
    } catch {
      finishSettingsSave('error');
    } finally {
      setSaving(false);
    }
  };

  const saveContentSettings = async () => {
    try {
      setSaving(true);
      const dirtyConfigs = imapConfigs.filter((cfg) => {
        const draft = signatureDrafts[cfg.id] || { html: '', text: '', marginTop: 10 };
        return (draft.html || '') !== (cfg.signatureHtml ?? '')
          || (draft.text || '') !== (cfg.signatureText ?? '')
          || (draft.marginTop ?? 10) !== (cfg.signatureMarginTop ?? 10);
      });
      const saved = await Promise.all(dirtyConfigs.map(async (cfg) => {
        const draft = signatureDrafts[cfg.id] || { html: '', text: '', marginTop: 10 };
        const payload = {
          signatureHtml: draft.html.trim() ? draft.html : null,
          signatureText: draft.text.trim() ? draft.text : null,
          signatureMarginTop: Math.max(0, Math.min(50, Math.round(draft.marginTop ?? 10))),
        };
        await inboxEmailApi.updateConfig(cfg.id, payload);
        return { id: cfg.id, payload };
      }));
      const savedById = new Map(saved.map(result => [result.id, result.payload]));
      setImapConfigs(current => current.map((cfg) => {
        const payload = savedById.get(cfg.id);
        return payload ? { ...cfg, ...payload } : cfg;
      }));
      finishSettingsSave('success');
    } catch {
      finishSettingsSave('error');
    } finally {
      setSaving(false);
    }
  };

  const toggleConfigAi = async (cfg: ImapConfig) => {
    try {
      setSavingAiConfig(cfg.id);
      const nextAiEnabledFrom = cfg.aiEnabledFrom ? null : new Date().toISOString();
      await inboxEmailApi.updateConfig(cfg.id, { aiEnabledFrom: nextAiEnabledFrom });
      setImapConfigs((prev) =>
        prev.map((c) => (c.id === cfg.id ? { ...c, aiEnabledFrom: nextAiEnabledFrom } : c)),
      );
      showToast(
        nextAiEnabledFrom
          ? `AI włączone dla skrzynki ${cfg.name || cfg.fromEmail}`
          : `AI wyłączone dla skrzynki ${cfg.name || cfg.fromEmail}`,
      );
    } catch {
      showToast('Błąd zapisu ustawień AI skrzynki', 'error');
    } finally {
      setSavingAiConfig(null);
    }
  };

  const loadPurgeLogs = async () => {
    try {
      setPurgeLogsLoading(true);
      const res = await messagesApi.getPurgeLogs({ limit: 100, page: 1 });
      setPurgeLogs(res.data.data || []);
      setPurgeLogsTotal(res.data.total || 0);
    } catch {
      showToast('Błąd ładowania dziennika RODO', 'error');
    } finally {
      setPurgeLogsLoading(false);
    }
  };

  const togglePurgeLogs = () => {
    const next = !purgeLogsOpen;
    setPurgeLogsOpen(next);
    if (next && purgeLogs.length === 0) loadPurgeLogs();
  };

  const updateCategoryConfig = async (category: string, data: { priority?: number; autoAction?: string | null; isActive?: boolean }) => {
    try {
      setSavingCategory(category);
      const res = await messagesApi.updateCategoryConfig(category, data);
      setCategoryConfigs(prev => {
        const existing = prev.findIndex(c => c.category === category);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = res.data.data;
          return updated;
        }
        return [...prev, res.data.data];
      });
    } catch {
      showToast('Błąd zapisu konfiguracji kategorii', 'error');
    } finally {
      setSavingCategory(null);
    }
  };

  const setAiLevel = async (level: number) => {
    if (!autoReplyConfig) return;
    try {
      setSavingAutoReply(true);
      const res = await messagesApi.setAiPermissionLevel(level);
      setAutoReplyConfig(res.data.data);
      showToast(`Poziom AI zmieniony na ${level}`);
    } catch {
      showToast('Błąd zmiany poziomu AI', 'error');
    } finally {
      setSavingAutoReply(false);
    }
  };


  const openNewTemplate = () => {
    templateSelectionRef.current = null;
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      subject: '',
      bodyHtml: '',
      storeId: '',
      categoryId: activeTemplateCategoryId === 'all' || activeTemplateCategoryId === 'uncategorized'
        ? ''
        : activeTemplateCategoryId,
      isActive: true,
      shortcut: '',
      autoSend: false,
    });
    setShowTemplateForm(true);
  };

  const openEditTemplate = (t: MessageTemplate) => {
    templateSelectionRef.current = null;
    setEditingTemplate(t);
    setTemplateForm({
      name: t.name,
      subject: t.subject || '',
      bodyHtml: t.bodyHtml,
      storeId: t.storeId || '',
      categoryId: t.categoryId || '',
      isActive: t.isActive,
      shortcut: t.shortcut || '',
      autoSend: t.autoSend || false,
    });
    setShowTemplateForm(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.bodyHtml) {
      showToast('Nazwa i treść szablonu są wymagane', 'error');
      return;
    }

    try {
      setSavingTemplate(true);
      const data = {
        name: templateForm.name,
        subject: templateForm.subject || undefined,
        bodyHtml: templateForm.bodyHtml,
        storeId: templateForm.storeId || null,
        categoryId: templateForm.categoryId || null,
        isActive: templateForm.isActive,
        shortcut: templateForm.shortcut || null,
        autoSend: templateForm.autoSend,
      };

      if (editingTemplate) {
        await messagesApi.updateTemplate(editingTemplate.id, data);
        showToast('Szablon zaktualizowany');
      } else {
        await messagesApi.createTemplate(data as any);
        showToast('Szablon utworzony');
      }

      setShowTemplateForm(false);
      setEditingTemplate(null);

      // Reload templates
      const res = await messagesApi.getAllTemplates();
      setTemplates(res.data.data || []);
    } catch {
      showToast('Błąd zapisu szablonu', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const createTemplateCategory = async () => {
    const name = templateCategoryName.trim();
    if (!name) {
      showToast('Podaj nazwę kategorii', 'error');
      return;
    }
    setSavingTemplateCategory(true);
    try {
      const { data } = await messagesApi.createTemplateCategory(name);
      setTemplateCategories(prev => [...prev, data.data]);
      setActiveTemplateCategoryId(data.data.id);
      setTemplateCategoryName('');
      setShowTemplateCategoryForm(false);
      showToast('Kategoria utworzona');
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Nie udało się utworzyć kategorii', 'error');
    } finally {
      setSavingTemplateCategory(false);
    }
  };

  const deleteTemplateCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await messagesApi.deleteTemplateCategory(categoryToDelete.id);
      setTemplateCategories(prev => prev.filter(category => category.id !== categoryToDelete.id));
      setTemplates(prev => prev.map(template => (
        template.categoryId === categoryToDelete.id
          ? { ...template, categoryId: null, category: null }
          : template
      )));
      if (activeTemplateCategoryId === categoryToDelete.id) setActiveTemplateCategoryId('all');
      setCategoryToDelete(null);
      showToast('Kategoria usunięta, szablony pozostały bez kategorii');
    } catch {
      showToast('Nie udało się usunąć kategorii', 'error');
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      await messagesApi.deleteTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      showToast('Szablon usunięty');
    } catch {
      showToast('Błąd usuwania szablonu', 'error');
    }
  };

  const openLoadSystemDialog = async () => {
    setLoadingSystemCount(true);
    setShowLoadSystemDialog(true);
    try {
      const { data } = await messagesApi.getSystemTemplatesAvailable();
      setSystemTemplatesCount(data.count);
    } catch {
      setSystemTemplatesCount(0);
      showToast('Nie udało się sprawdzić systemowych szablonów', 'error');
    } finally {
      setLoadingSystemCount(false);
    }
  };

  const confirmLoadSystem = async () => {
    setImportingSystem(true);
    try {
      const { data } = await messagesApi.loadSystemTemplates();
      const res = await messagesApi.getAllTemplates();
      const categoriesRes = await messagesApi.getTemplateCategories();
      setTemplates(res.data.data || []);
      setTemplateCategories(categoriesRes.data.data || []);
      const { imported, skipped, importedCategories } = data.data;
      if (imported > 0) {
        const categoryInfo = importedCategories > 0
          ? ` Dodano też ${importedCategories} ${importedCategories === 1 ? 'kategorię' : 'kategorie'}.`
          : '';
        showToast(
          skipped > 0
            ? `Wgrano ${imported} szablonów (${skipped} pominięto - już istnieją).${categoryInfo}`
            : `Wgrano ${imported} szablonów.${categoryInfo}`
        );
      } else if (importedCategories > 0) {
        showToast(`Szablony były już na koncie. Dodano ${importedCategories} ${importedCategories === 1 ? 'kategorię' : 'kategorie'} i przypisano istniejące szablony.`);
      } else {
        showToast('Wszystkie systemowe szablony już są na Twoim koncie.', 'error');
      }
      setShowLoadSystemDialog(false);
    } catch {
      showToast('Błąd wczytywania systemowych szablonów', 'error');
    } finally {
      setImportingSystem(false);
    }
  };

  // --- Labels CRUD ---
  const openNewLabel = () => {
    setEditingLabel(null);
    setLabelForm({ name: '', color: '#6B7280' });
    setShowLabelForm(true);
  };

  const openEditLabel = (label: InboxLabel) => {
    setEditingLabel(label);
    setLabelForm({ name: label.name, color: label.color });
    setShowLabelForm(true);
  };

  const saveLabel = async () => {
    if (!labelForm.name.trim()) {
      showToast('Nazwa etykiety jest wymagana', 'error');
      return;
    }
    try {
      if (editingLabel) {
        await messagesApi.updateLabel(editingLabel.id, labelForm);
        showToast('Etykieta zaktualizowana');
      } else {
        await messagesApi.createLabel(labelForm);
        showToast('Etykieta utworzona');
      }
      setShowLabelForm(false);
      const res = await messagesApi.getLabels();
      setLabelsList(res.data.data || []);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Błąd zapisu etykiety', 'error');
    }
  };

  const deleteLabel = async (id: string) => {
    try {
      await messagesApi.deleteLabel(id);
      setLabelsList(prev => prev.filter(l => l.id !== id));
      showToast('Etykieta usunięta');
    } catch {
      showToast('Błąd usuwania etykiety', 'error');
    }
  };

  // --- Rules CRUD ---
  const openNewRule = () => {
    setEditingRule(null);
    setRuleForm({
      name: '',
      storeId: null,
      conditions: [{ field: 'subject', operator: 'contains', value: '' }],
      conditionLogic: 'all',
      actions: [{ type: 'mark_as_read', config: {} }],
      isActive: true,
      stopProcessing: false,
    });
    setShowRuleForm(true);
  };

  const openEditRule = (rule: InboxRuleItem) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      storeId: rule.storeId || null,
      conditions: rule.conditions,
      conditionLogic: rule.conditionLogic || 'all',
      actions: rule.actions,
      isActive: rule.isActive,
      stopProcessing: rule.stopProcessing,
    });
    setShowRuleForm(true);
  };

  const saveRule = async () => {
    if (!ruleForm.name.trim()) {
      showToast('Nazwa reguły jest wymagana', 'error');
      return;
    }
    if (ruleForm.conditions.some(c => !c.value && c.field !== 'has_attachments')) {
      showToast('Wszystkie warunki muszą mieć wartość', 'error');
      return;
    }
    try {
      if (editingRule) {
        await messagesApi.updateRule(editingRule.id, ruleForm);
        showToast('Reguła zaktualizowana');
      } else {
        await messagesApi.createRule(ruleForm);
        showToast('Reguła utworzona');
      }
      setShowRuleForm(false);
      const res = await messagesApi.getRules();
      setRulesList(res.data.data || []);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Błąd zapisu reguły', 'error');
    }
  };

  const deleteRule = async (id: string) => {
    try {
      await messagesApi.deleteRule(id);
      setRulesList(prev => prev.filter(r => r.id !== id));
      showToast('Reguła usunięta');
    } catch {
      showToast('Błąd usuwania reguły', 'error');
    }
  };

  const toggleRuleActive = async (rule: InboxRuleItem) => {
    try {
      await messagesApi.updateRule(rule.id, { isActive: !rule.isActive });
      setRulesList(prev => prev.map(r => r.id === rule.id ? { ...r, isActive: !r.isActive } : r));
    } catch {
      showToast('Błąd aktualizacji', 'error');
    }
  };

  const applyRuleToExisting = async (rule: InboxRuleItem) => {
    if (applyingRuleId) return;
    setApplyingRuleId(rule.id);
    try {
      const res = await messagesApi.applyRule(rule.id);
      const result = (res.data?.data ?? res.data ?? res) as { processed: number; matched: number };
      showToast(`Zastosowano: ${result.matched} z ${result.processed} wiadomości dopasowanych`);
      // Refresh rules list to update matchCount
      const rulesRes = await messagesApi.getRules();
      setRulesList(((rulesRes.data as { data: InboxRuleItem[] })?.data ?? (rulesRes.data as InboxRuleItem[])) || []);
    } catch {
      showToast('Błąd stosowania reguły', 'error');
    } finally {
      setApplyingRuleId(null);
    }
  };

  const moveRulePosition = async (rule: InboxRuleItem, direction: 'up' | 'down') => {
    const idx = rulesList.findIndex(r => r.id === rule.id);
    if (direction === 'up' && idx <= 0) return;
    if (direction === 'down' && idx >= rulesList.length - 1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newList = [...rulesList];
    [newList[idx], newList[swapIdx]] = [newList[swapIdx], newList[idx]];
    setRulesList(newList);
    try {
      await messagesApi.reorderRules(newList.map((r, i) => ({ id: r.id, position: i })));
    } catch {
      showToast('Błąd zmiany kolejności', 'error');
    }
  };

  const addCondition = () => {
    setRuleForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: 'subject', operator: 'contains', value: '' }],
    }));
  };

  const removeCondition = (idx: number) => {
    setRuleForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== idx),
    }));
  };

  const updateCondition = (idx: number, key: string, val: string) => {
    setRuleForm(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => {
        if (i !== idx) return c;
        const updated = { ...c, [key]: val };
        // Auto-set value for has_attachments field
        if (key === 'field' && val === 'has_attachments') {
          updated.value = 'true';
          updated.operator = 'equals';
        }
        return updated;
      }),
    }));
  };

  const addAction = () => {
    setRuleForm(prev => ({
      ...prev,
      actions: [...prev.actions, { type: 'mark_as_read', config: {} }],
    }));
  };

  const removeAction = (idx: number) => {
    setRuleForm(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== idx),
    }));
  };

  const updateAction = (idx: number, key: string, val: string) => {
    setRuleForm(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => {
        if (i !== idx) return a;
        if (key === 'type') return { type: val, config: {} };
        return { ...a, config: { ...a.config, [key]: val } };
      }),
    }));
  };

  // Opcje dla formularza reguł
  const conditionFieldOptions = [
    { value: 'sender', label: 'Nadawca (email)' },
    { value: 'sender_name', label: 'Nazwa nadawcy' },
    { value: 'subject', label: 'Temat' },
    { value: 'to', label: 'Odbiorca' },
    { value: 'body', label: 'Treść' },
    { value: 'has_attachments', label: 'Ma załączniki' },
  ];

  const conditionOperatorOptions = [
    { value: 'contains', label: 'zawiera' },
    { value: 'not_contains', label: 'nie zawiera' },
    { value: 'equals', label: 'jest równe' },
    { value: 'not_equals', label: 'nie jest równe' },
    { value: 'starts_with', label: 'zaczyna się od' },
    { value: 'ends_with', label: 'kończy się na' },
    { value: 'matches_regex', label: 'pasuje do regex' },
  ];

  // 'move_to_folder' tymczasowo wyłączone — po migracji do natywnych folderów IMAP
  // reguły nie mogą już celować w custom folders. Przenoszenie do folderu IMAP
  // z poziomu reguł zostanie dodane gdy FolderTreePanel będzie dostępny jako picker.
  const actionTypeOptions = [
    { value: 'mark_as_read', label: 'Oznacz jako przeczytane' },
    { value: 'mark_as_starred', label: 'Oznacz gwiazdką' },
    { value: 'add_label', label: 'Dodaj etykietę' },
    { value: 'archive', label: 'Archiwizuj' },
    { value: 'delete', label: 'Przenieś do kosza' },
  ];

  // ─── Subpage routing (overview + categories, spójnie z SettingsPage Logistyki) ───
  const location = useLocation();
  const subpage = (location.pathname.match(/\/messages\/settings\/?(.*)/)?.[1] || '').replace(/\/$/, '');
  const currentSection = MESSAGES_SETTINGS_SECTIONS.find(s => s.slug === subpage);
  const saveCurrentSettings = () => {
    if (subpage === 'general') return void saveGeneralSettings();
    if (subpage === 'automation') return void saveAutomationSettings();
    if (subpage === 'content') return void saveContentSettings();
    if (subpage === 'ai' || subpage === 'rodo') return void saveAllAiSettings();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  // ─── Overview (landing z grid kart — wzorzec Logistyki) ────────────────
  if (subpage === '') {
    return (
      <div className="space-y-6">
        <div className="animate-fade-in">
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 border border-accent-primary/30 flex items-center justify-center">
              <Settings className="w-5 h-5 text-accent-primary" />
            </div>
            Ustawienia wiadomości
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Zarządzaj konfiguracją modułu Wiadomości
          </p>
        </div>

        <div className="card p-4 bg-surface-raised/30 border-border-subtle animate-slide-up">
          <p className="text-sm text-text-secondary">
            Konfiguracja kont e-mail (IMAP) oraz integracji Facebook znajduje się w zakładce{' '}
            <a href="/integrations" className="text-accent-primary hover:underline">Integracje</a>
            {' '}dostępnej z menu w prawym górnym rogu.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MESSAGES_SETTINGS_SECTIONS.map(section => (
            <Link
              key={section.slug}
              to={`/messages/settings/${section.slug}`}
              className="card hover:border-accent-primary/30 hover:shadow-accent-primary/20/5 transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${section.bgColor} flex-shrink-0`}>
                  <section.icon className={`w-5 h-5 ${section.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-text-primary group-hover:text-accent-primary transition-colors">
                    {section.title}
                  </h3>
                  <p className="text-xs text-text-muted mt-1 leading-relaxed">
                    {section.description}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-text-disabled group-hover:text-accent-primary transition-colors flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // ─── Subpage (AI / Content / Rules / Accounts / RODO) ───────────────────
  return (
    <div className="space-y-6">
      {/* Breadcrumb + header per sekcja */}
      <div className="animate-fade-in">
        <BackLink to="/messages/settings" label="Wróć do ustawień" />
        <PageHeader
          icon={currentSection?.icon}
          title={currentSection?.title || 'Ustawienia'}
          subtitle={currentSection?.description}
        />
      </div>

      {/* Sekcja: Asystent AI — rozbita na osobne SettingsSection per kategoria */}
      {subpage === 'ai' && (
      <div className="space-y-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
        {/* 1. Model AI */}
        <SettingsSection
          icon={Sparkles}
          title="Model AI"
          subtitle="Wybierz preferowanego dostawcę i włącz personalizację szablonów"
        >
          <div>
            <label className="text-xs text-text-secondary mb-2 block">Preferowany model AI</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {providers.map(provider => (
                <button
                  key={provider.name}
                  onClick={() => setSettings(prev => prev ? { ...prev, preferredProvider: provider.name } : null)}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    settings?.preferredProvider === provider.name
                      ? 'border-accent-primary/50 bg-accent-primary/10'
                      : 'border-border-subtle bg-surface-raised/50 hover:border-border'
                  }`}
                >
                  <p className={`text-sm font-semibold ${
                    settings?.preferredProvider === provider.name ? 'text-accent-primary' : 'text-text-primary'
                  }`}>
                    {provider.displayName}
                  </p>
                  <p className="text-xs text-text-muted mt-1">{provider.modelId}</p>
                  {settings?.preferredProvider === provider.name && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-accent-primary">
                      <Check className="w-3 h-3" /> Aktywny
                    </div>
                  )}
                </button>
              ))}

              {providers.length === 0 && (
                <div className="col-span-3">
                  <EmptyState
                    icon={AlertCircle}
                    title="Brak skonfigurowanych dostawców AI"
                    description="Konfigurację AI wykonuje administrator w Panelu admina."
                    size="sm"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="-mx-6 mt-6">
            <ToggleRow
              label="Personalizacja szablonów AI"
              description="Po wybraniu szablonu, AI dostosuje treść do kontekstu rozmowy (~500-1000 tokenów)"
              checked={Boolean(settings?.aiTemplatePersonalization)}
              onChange={(next) => setSettings(prev => prev ? { ...prev, aiTemplatePersonalization: next } : null)}
            />
          </div>
        </SettingsSection>

        {/* 2. Prompt systemowy */}
        <SettingsSection
          icon={FileText}
          title="Prompt systemowy"
          subtitle="Instrukcje które AI otrzyma przy generowaniu odpowiedzi. Baza wiedzy, historia wątku i dane zamówień klienta są dołączane automatycznie."
          actions={
            <Button
              onClick={() => setShowPromptModal(true)}
              variant="secondary"
              size="sm"
              leftIcon={<FileText />}
              title="Otwórz edytor promptu"
            >
              Edytuj prompt
            </Button>
          }
        >
          <div className="flex items-center gap-1 text-xxs text-text-disabled">
            <Lock className="w-3 h-3" /> Zablokowany (edycja na własną odpowiedzialność)
          </div>
        </SettingsSection>

        {/* Full-screen modal edytora promptu — textarea + lock/unlock/reset inline.
            Renderowany przez Portal, bo rodzic sekcji "card animate-slide-up" ma
            transform w keyframe → łamał position:fixed (modal kotwiczył się do
            transformowanego rodzica, nie viewport → wychodził poza środek ekranu). */}
        {showPromptModal && createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowPromptModal(false); }}
          >
            <div className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-scale-in">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-accent-primary" />
                  <h3 className="text-base font-semibold text-text-primary">Prompt systemowy AI</h3>
                  {!promptEditable && (
                    <span className="flex items-center gap-1 text-xxs text-text-disabled ml-2">
                      <Lock className="w-3 h-3" /> Zablokowany
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowPromptModal(false)}
                  className="p-1.5 rounded hover:bg-surface-hover/50 text-text-secondary hover:text-text-primary transition-colors"
                  title="Zamknij"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 px-5 py-2 border-b border-border-subtle bg-surface-base/30">
                {!promptEditable ? (
                  <button
                    onClick={() => setShowPromptWarning(true)}
                    className="text-xs text-text-secondary hover:text-warning flex items-center gap-1 px-2 py-1 rounded transition-colors"
                  >
                    <Unlock className="w-3 h-3" /> Odblokuj edycję
                  </button>
                ) : (
                  <button
                    onClick={() => setPromptEditable(false)}
                    className="text-xs text-text-secondary hover:text-accent-primary flex items-center gap-1 px-2 py-1 rounded transition-colors"
                  >
                    <Lock className="w-3 h-3" /> Zablokuj edycję
                  </button>
                )}
                <button
                  onClick={() => {
                    setSettings(prev => prev ? { ...prev, systemPrompt: DEFAULT_SYSTEM_PROMPT } : null);
                    setPromptEditable(false);
                  }}
                  className="text-xs text-text-secondary hover:text-accent-primary flex items-center gap-1 px-2 py-1 rounded transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> Przywróć domyślny
                </button>
                <span className="ml-auto text-xs text-text-disabled">
                  {(settings?.systemPrompt || '').length} / 10 000
                </span>
              </div>
              <div className="flex-1 overflow-auto p-5">
                <textarea
                  value={settings?.systemPrompt || ''}
                  onChange={(e) => setSettings(prev => prev ? { ...prev, systemPrompt: e.target.value } : null)}
                  className={`input w-full text-sm resize-none min-h-[400px] font-mono ${!promptEditable ? 'opacity-60 cursor-not-allowed' : ''}`}
                  placeholder="Wpisz instrukcje dla AI..."
                  rows={20}
                  maxLength={10000}
                  readOnly={!promptEditable}
                />
                <p className="text-xs text-text-disabled mt-2">
                  Baza wiedzy, historia wątku i dane zamówień klienta są dołączane automatycznie - nie musisz ich tu powielać.
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-border-subtle">
                <Button onClick={() => setShowPromptModal(false)}>
                  Zamknij
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

        {/* Prompt edit warning modal */}
        {showPromptWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-surface-raised border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 animate-scale-in">
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-warning/10 border border-warning/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-warning" />
                  </div>
                  <h3 className="text-base font-semibold text-text-primary">Edycja głównego promptu AI</h3>
                </div>
                <p className="text-sm text-text-secondary mb-2">
                  Ten prompt steruje logiką odpowiedzi AI. Zmiana może wpłynąć na jakość generowanych odpowiedzi.
                </p>
                <p className="text-sm text-text-secondary">
                  Sugerujemy nie edytować - dodawaj informacje w <span className="text-accent-primary">Bazie wiedzy</span>.
                  Jeśli musisz edytować, dodawaj tylko priorytetowe instrukcje.
                </p>
              </div>
              <div className="flex gap-3 p-4 border-t border-border-subtle">
                <Button
                  onClick={() => {
                    setPromptEditable(true);
                    setShowPromptWarning(false);
                  }}
                  fullWidth
                >
                  Rozumiem, chcę edytować
                </Button>
                <Button
                  onClick={() => setShowPromptWarning(false)}
                  variant="secondary"
                  fullWidth
                >
                  Anuluj
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 3. Kategorie AI */}
        <SettingsSection
          icon={Tag}
          title="Kategorie AI"
          subtitle="Konfiguracja auto-akcji per kategoria. Auto-rozwiąż: oznacza wiadomość jako rozwiązaną i archiwizuje na IMAP. Oflaguj: zaznacza do uwagi."
        >
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'tracking', label: 'Tracking', color: 'text-info bg-info-bg border-info-border' },
              { key: 'return', label: 'Zwrot', color: 'text-warning bg-warning-bg border-warning-border' },
              { key: 'complaint', label: 'Reklamacja', color: 'text-danger bg-danger-bg border-danger-border' },
              { key: 'question', label: 'Pytanie', color: 'text-success bg-success-bg border-success-border' },
              { key: 'payment', label: 'Płatność', color: 'text-warning bg-warning-bg border-warning-border' },
              { key: 'order_confirmation', label: 'Potwierdzenie', color: 'text-accent-primary bg-accent-primary/10 border-accent-primary/30' },
              { key: 'spam', label: 'Spam', color: 'text-text-secondary bg-surface-hover border-border' },
              { key: 'other', label: 'Inne', color: 'text-text-secondary bg-surface-hover border-border' },
            ].map(cat => {
              const cfg = categoryConfigs.find(c => c.category === cat.key);
              return (
                <CategoryAutoActionRow
                  key={cat.key}
                  badgeLabel={cat.label}
                  badgeClassName={cat.color}
                  priority={cfg?.priority ?? 50}
                  onPriorityChange={(priority) => updateCategoryConfig(cat.key, { priority })}
                  autoAction={cfg?.autoAction || 'none'}
                  onAutoActionChange={(autoAction) => updateCategoryConfig(cat.key, { autoAction })}
                  options={[
                    { value: 'none', label: 'Brak' },
                    { value: 'auto_resolve', label: 'Auto-rozwiąż' },
                    { value: 'flag', label: 'Oflaguj' },
                  ]}
                  saving={savingCategory === cat.key}
                />
              );
            })}
          </div>
        </SettingsSection>

        {/* 4. Poziom uprawnień AI */}
        <SettingsSection
          icon={Shield}
          title="Poziom uprawnień AI"
          subtitle="Kontroluj co AI może robić z wiadomościami"
          actions={
            <button
              type="button"
              onClick={() => setShowRodoPopover((v) => !v)}
              className="relative w-5 h-5 rounded-full bg-warning/20 border border-warning/40 text-warning text-xxs font-bold hover:bg-warning/30 transition-colors flex items-center justify-center"
              title="Informacja RODO"
              aria-label="Informacja RODO"
            >
              ?
            </button>
          }
        >
          {/* RODO disclosure — discrete popover, toggle ikonką ? */}
          {showRodoPopover && (
            <Alert
              variant="warning"
              title="Uwaga RODO"
              onClose={() => setShowRodoPopover(false)}
              className="mb-4"
            >
              Włączenie poziomu {'\u2265'} 1 oznacza, że treść Twoich maili
              (temat + pierwsze 1000 znaków body) będzie wysyłana do zewnętrznego dostawcy AI
              (Claude / Gemini / ChatGPT) w celu klasyfikacji. Na poziomach 3-4 pełna treść
              idzie również do generowania szkiców odpowiedzi. Poziom 0 wyłącza całkowicie
              transmisję danych do AI - skrzynka działa bez kategoryzacji i szkiców.
            </Alert>
          )}

          {autoReplyConfig && (
            <div className="space-y-4">
              {/* Level selector — 5 cards with feature bullets */}
              <div className="grid grid-cols-5 gap-2">
                {([
                  {
                    level: 0, name: 'Wyłączony', icon: PowerOff, color: 'text-text-muted',
                    desc: 'AI jest całkowicie wyłączone. Żadne wiadomości nie będą analizowane ani przetwarzane przez sztuczną inteligencję.',
                    features: [false, false, false, false],
                  },
                  {
                    level: 1, name: 'Obserwator', icon: Eye, color: 'text-info',
                    desc: 'AI analizuje wiadomości w tle i porządkuje skrzynkę. Pomaga szybciej znaleźć ważne maile dzięki kategoriom i priorytetom.',
                    features: [true, false, false, false],
                  },
                  {
                    level: 2, name: 'Asystent', icon: Zap, color: 'text-warning',
                    desc: 'AI kategoryzuje wiadomości i automatycznie wykonuje proste akcje. Oszczędza czas na powtarzalnych czynnościach jak archiwizacja spamu.',
                    features: [true, true, false, false],
                  },
                  {
                    level: 3, name: 'Współpracownik', icon: Users, color: 'text-success',
                    desc: 'AI przygotowuje szkice odpowiedzi do Twojej akceptacji. Masz pełną kontrolę, nic nie zostanie wysłane bez Twojej zgody.',
                    features: [true, true, true, false],
                  },
                  {
                    level: 4, name: 'Dyrektor', icon: Bot, color: 'text-danger',
                    desc: 'AI samodzielnie odpowiada na wiadomości o wysokiej pewności. Idealny dla powtarzalnych zapytań: tracking, statusy zamówień etc.',
                    features: [true, true, true, true],
                  },
                ] as const).map(l => {
                  const isActive = autoReplyConfig.aiPermissionLevel === l.level;
                  const featureLabels = [
                    'Kategoryzacja i priorytety',
                    'Auto-akcje (rozwiąż, oflaguj)',
                    'Generowanie draftów',
                    'Auto-wysyłka odpowiedzi',
                  ];
                  return (
                    <button
                      key={l.level}
                      onClick={() => setAiLevel(l.level)}
                      disabled={savingAutoReply}
                      className={`relative p-3 rounded-xl border text-left transition-all ${
                        isActive
                          ? 'bg-accent-primary/5 border-accent-primary/40 ring-1 ring-accent-primary/20'
                          : 'bg-surface-raised/50 border-border-subtle hover:border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <l.icon className={`w-4 h-4 ${isActive ? 'text-accent-primary' : l.color}`} />
                        <span className={`text-xs font-semibold ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>
                          Poziom {l.level}
                        </span>
                      </div>
                      <p className={`text-sm font-medium mb-1 ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>{l.name}</p>
                      <p className="text-xxs text-text-muted leading-relaxed mb-2">{l.desc}</p>
                      <div className="space-y-1 border-t border-border-subtle pt-2">
                        {featureLabels.map((label, i) => {
                          const enabled = l.features[i];
                          return (
                            <div key={i} className={`flex items-center gap-1.5 text-xxs ${enabled ? 'text-text-secondary' : 'text-text-disabled'}`}>
                              {enabled ? (
                                <Check className="w-3 h-3 text-success flex-shrink-0" />
                              ) : (
                                <X className="w-3 h-3 text-text-disabled flex-shrink-0" />
                              )}
                              <span className={enabled ? '' : 'line-through'}>{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Confidence & limits — only when draft generation or auto-send is enabled */}
              {(autoReplyConfig.allowDraftGeneration || autoReplyConfig.allowAutoSend) && (
                <>
                  <div className={`grid ${autoReplyConfig.allowAutoSend ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
                    <div className="p-3 rounded-xl bg-surface-raised/30 border border-border-subtle">
                      <label className="text-xs text-text-secondary block mb-1.5">Przegląd wiadomości - Stopień pewności decyzji</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0.1}
                          max={0.9}
                          step={0.05}
                          value={autoReplyConfig.reviewMinConfidence}
                          onChange={e => setAutoReplyConfig({ ...autoReplyConfig, reviewMinConfidence: parseFloat(e.target.value) })}
                          className="flex-1 accent-accent-primary"
                        />
                        <span className="text-xs text-text-primary w-10 text-right">{(autoReplyConfig.reviewMinConfidence * 100).toFixed(0)}%</span>
                      </div>
                      <p className="text-xxs text-text-muted mt-1">Ustaw próg pewności na podstawie którego Asystent AI podejmuje decyzję tworzenia szkiców wiadomości. Poniżej progu nie wygeneruje szkicu.</p>
                    </div>
                    {autoReplyConfig.allowAutoSend && (
                      <div className="p-3 rounded-xl bg-surface-raised/30 border border-border-subtle">
                        <label className="text-xs text-text-secondary block mb-1.5">Automatyczne odpowiedzi - Stopień pewności decyzji</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min={0.5}
                            max={1}
                            step={0.05}
                            value={autoReplyConfig.globalMinConfidence}
                            onChange={e => setAutoReplyConfig({ ...autoReplyConfig, globalMinConfidence: parseFloat(e.target.value) })}
                            className="flex-1 accent-accent-primary"
                          />
                          <span className="text-xs text-text-primary w-10 text-right">{(autoReplyConfig.globalMinConfidence * 100).toFixed(0)}%</span>
                        </div>
                        <p className="text-xxs text-text-muted mt-1">Ustaw próg pewności na podstawie którego Asystent AI automatycznie odpisze na wiadomość. Poniżej tego progu wiadomość trafi do &quot;Przeglądu wiadomości&quot; w celu utworzenia ewentualnego szkicu.</p>
                      </div>
                    )}
                  </div>

                  {autoReplyConfig.allowAutoSend && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-surface-raised/30 border border-border-subtle">
                        <label className="text-xs text-text-secondary block mb-1.5">Max auto-wysyłek / dzień</label>
                        <Input
                          type="number"
                          min={1}
                          max={500}
                          value={autoReplyConfig.maxDailyAutoReplies}
                          onChange={e => setAutoReplyConfig({ ...autoReplyConfig, maxDailyAutoReplies: parseInt(e.target.value) || 50 })}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </SettingsSection>

        {/* 5. Automatyzacje AI — lista ToggleRow, widoczna gdy drafts/auto-send włączone */}
        {autoReplyConfig && (autoReplyConfig.allowDraftGeneration || autoReplyConfig.allowAutoSend) && (
          <>
            <SettingsSection
              icon={Zap}
              title="Automatyzacje AI"
              subtitle="Reguły kolejki i draftów"
              flushContent
            >
              <ToggleRow
                label="Kolejka do przeglądu"
                description="Wiadomości o średniej pewności trafiają do ręcznego przeglądu"
                checked={autoReplyConfig.reviewQueueEnabled}
                onChange={(next) => setAutoReplyConfig({ ...autoReplyConfig, reviewQueueEnabled: next })}
              />
              <ToggleRow
                label="Drafty AI tylko dla Skrzynki odbiorczej"
                description={'Gdy włączone, AI nie generuje draftów dla wiadomości przeniesionych do folderów własnych (np. "Do rozwiązania") ani do Archiwum. Opcja zmniejsza zużycie tokenów - działa przy ręcznym przenoszeniu maili w innych klientach (Spark itp.) oraz dla automatycznego przeglądu maili bez kategorii.'}
                checked={autoReplyConfig.draftsOnlyForInbox}
                onChange={(next) => setAutoReplyConfig({ ...autoReplyConfig, draftsOnlyForInbox: next })}
              />
              <ToggleRow
                label={'Auto-oznacz jako Rozwiązane po wysłaniu draftu'}
                description={'Po kliknięciu „Zatwierdź i wyślij" wątek automatycznie dostaje status „Rozwiązane". Jeśli wybierzesz folder w Statusach konwersacji, mail zostanie tam przeniesiony. Bez mapowania pozostanie w Odebranych.'}
                checked={autoReplyConfig.autoResolveOnSend}
                onChange={(next) => setAutoReplyConfig({ ...autoReplyConfig, autoResolveOnSend: next })}
              />
              <ToggleRow
                label="Usuń draft AI gdy wiadomość znika z Inboxa"
                description="Gdy mail zostaje przeniesiony do Kosza, Archiwum lub własnego folderu, powiązany pending draft zostaje automatycznie usunięty z kolejki review. Bez tego drafty wiszą nawet dla maili z którymi już skończyłeś pracować."
                checked={autoReplyConfig.deleteDraftOnMessageGone}
                onChange={(next) => setAutoReplyConfig({ ...autoReplyConfig, deleteDraftOnMessageGone: next })}
              />
              {/* Retroaktywny cleanup: opcja dopiero włączona nie czyści istniejących
                  draftów dla maili już poza Inboxem. User klika ten button żeby
                  posprzątać jednorazowo. */}
              <ToggleRow
                label="Masz już stare drafty dla maili poza Inboxem?"
                description="Kliknij, żeby wyczyścić teraz."
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await messagesApi.cleanupOrphanedDrafts();
                        const cleaned = (res as { data?: { cleaned?: number } })?.data?.cleaned ?? 0;
                        if (cleaned > 0) {
                          showToast(`Wyczyszczono ${cleaned} ${cleaned === 1 ? 'drafta' : 'draftów'} dla maili już poza Inboxem`);
                        } else {
                          showToast('Wszystkie drafty AI są aktualne');
                        }
                      } catch {
                        showToast('Błąd przy czyszczeniu draftów', 'error');
                      }
                    }}
                  >
                    Wyczyść teraz
                  </Button>
                }
              />
            </SettingsSection>

            {/* 6. Statystyki dzisiaj */}
            {autoReplyStats && (
              <SettingsSection
                icon={BarChart2}
                title="Statystyki dzisiaj"
              >
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-success font-semibold">{autoReplyStats.todayAutoSent}</span>
                    <span className="text-text-muted ml-1">auto-wysłanych</span>
                  </div>
                  <div>
                    <span className="text-accent-primary font-semibold">{autoReplyStats.todayApproved}</span>
                    <span className="text-text-muted ml-1">zatwierdzonych</span>
                  </div>
                  <div>
                    <span className="text-danger font-semibold">{autoReplyStats.todayRejected}</span>
                    <span className="text-text-muted ml-1">odrzuconych</span>
                  </div>
                  <div>
                    <span className="text-warning font-semibold">{autoReplyStats.totalPending}</span>
                    <span className="text-text-muted ml-1">oczekujących</span>
                  </div>
                </div>
              </SettingsSection>
            )}
          </>
        )}
      </div>
      )}

      {/* Sekcja: Szablony wiadomości */}
      {subpage === 'content' && (
      <div className="card p-5 space-y-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20 border border-accent-primary/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Szablony wiadomości</h2>
              <p className="text-xs text-text-secondary">Szybkie odpowiedzi — globalne lub per sklep</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={openLoadSystemDialog}
              leftIcon={<Download />}
            >
              Szablony systemowe
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowTemplateCategoryForm(true)}
              leftIcon={<Plus />}
            >
              Nowa kategoria
            </Button>
            <Button
              onClick={openNewTemplate}
              leftIcon={<Plus />}
            >
              Nowy szablon
            </Button>
          </div>
        </div>

        {showTemplateCategoryForm && (
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface-raised/60 p-3 animate-fade-in">
            <div className="min-w-56 flex-1">
              <label className="mb-1 block text-xs text-text-secondary">Nazwa nowej kategorii</label>
              <Input
                value={templateCategoryName}
                onChange={event => setTemplateCategoryName(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void createTemplateCategory();
                  }
                }}
                autoFocus
                maxLength={80}
                placeholder="np. Dostawa i tracking"
              />
            </div>
            <Button onClick={createTemplateCategory} loading={savingTemplateCategory} leftIcon={<Save />}>
              Dodaj kategorię
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowTemplateCategoryForm(false);
                setTemplateCategoryName('');
              }}
            >
              Anuluj
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-1.5" aria-label="Kategorie szablonów">
          <button
            type="button"
            onClick={() => setActiveTemplateCategoryId('all')}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeTemplateCategoryId === 'all'
                ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30 hover:text-text-primary'
            }`}
          >
            Wszystkie <span className="ml-1 text-text-muted">{templates.length}</span>
          </button>
          {templateCategories.map(category => (
            <div
              key={category.id}
              className={`flex items-center overflow-hidden rounded-full border transition-colors ${
                activeTemplateCategoryId === category.id
                  ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                  : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveTemplateCategoryId(category.id)}
                className="py-1.5 pl-3 pr-1 text-xs font-medium"
              >
                {category.name} <span className="ml-1 text-text-muted">{templates.filter(template => template.categoryId === category.id).length}</span>
              </button>
              <button
                type="button"
                onClick={() => setCategoryToDelete(category)}
                className="mr-1 rounded-full p-1 text-text-muted hover:bg-danger/10 hover:text-danger"
                title={`Usuń kategorię ${category.name}`}
                aria-label={`Usuń kategorię ${category.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {templates.some(template => !template.categoryId) && (
            <button
              type="button"
              onClick={() => setActiveTemplateCategoryId('uncategorized')}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTemplateCategoryId === 'uncategorized'
                  ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                  : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30 hover:text-text-primary'
              }`}
            >
              Bez kategorii <span className="ml-1 text-text-muted">{templates.filter(template => !template.categoryId).length}</span>
            </button>
          )}
        </div>

        {/* Template form */}
        {showTemplateForm && (
          <div className="bg-surface-raised/60 rounded-xl border border-border p-4 space-y-3 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-text-primary">
                {editingTemplate ? 'Edytuj szablon' : 'Nowy szablon'}
              </h3>
              <button onClick={() => setShowTemplateForm(false)} className="btn-ghost p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Nazwa szablonu *</label>
                <Input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="np. Potwierdzenie odbioru"
                />
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Sklep</label>
                <Select
                  value={templateForm.storeId || ''}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, storeId: e.target.value || null }))}
                >
                  <option value="">Globalny (wszystkie sklepy)</option>
                  {stores.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Kategoria</label>
                <Select
                  value={templateForm.categoryId || ''}
                  onChange={(event) => setTemplateForm(prev => ({ ...prev, categoryId: event.target.value || null }))}
                >
                  <option value="">Bez kategorii</option>
                  {templateCategories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs text-text-secondary mb-1 block">Temat (opcjonalny)</label>
              <Input
                type="text"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="np. Re: Twoje zamówienie"
              />
            </div>

            <div>
              <label className="text-xs text-text-secondary mb-1 block">Treść szablonu *</label>
              <div className="bg-surface-raised/50 rounded-xl overflow-hidden border border-border-subtle">
                <ReactQuill
                  ref={templateEditorRef}
                  theme="snow"
                  value={templateForm.bodyHtml}
                  onChange={(val) => setTemplateForm(prev => ({ ...prev, bodyHtml: val }))}
                  onChangeSelection={(range) => {
                    if (range) templateSelectionRef.current = range;
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Wpisz treść szablonu..."
                  className="inbox-editor"
                />
              </div>

              <MessageTemplateVariablePicker
                onInsert={(code) => {
                  const editor = templateEditorRef.current?.getEditor();
                  if (!editor) return;
                  const range = templateSelectionRef.current || editor.getSelection();
                  const index = Math.min(range?.index ?? Math.max(0, editor.getLength() - 1), Math.max(0, editor.getLength() - 1));
                  const selectionLength = Math.min(range?.length ?? 0, Math.max(0, editor.getLength() - 1 - index));
                  if (selectionLength > 0) editor.deleteText(index, selectionLength, 'user');
                  editor.insertText(index, code, 'user');
                  editor.setSelection(index + code.length, 0, 'silent');
                  templateSelectionRef.current = { index: index + code.length, length: 0 };
                  setTemplateForm(prev => ({ ...prev, bodyHtml: editor.root.innerHTML }));
                }}
              />
            </div>

            {/* Slash command & auto-send */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Slash command (opcjonalnie)</label>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-text-muted">/</span>
                  <Input
                    type="text"
                    placeholder="np. tracking"
                    className="flex-1"
                    value={templateForm.shortcut || ''}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, shortcut: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') || null }))}
                  />
                </div>
                <p className="text-xxs text-text-disabled mt-0.5">Wpisz /{templateForm.shortcut || 'nazwa'} w edytorze, by szybko wstawić szablon</p>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateForm.autoSend}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, autoSend: e.target.checked }))}
                    className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                  />
                  <span className="text-sm text-text-primary">Auto-wyślij</span>
                </label>
                <span className="text-xxs text-text-muted">(wyślij automatycznie po wyborze szablonu)</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateForm.isActive}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                <span className="text-sm text-text-primary">Aktywny</span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={saveTemplate}
                disabled={savingTemplate}
                loading={savingTemplate}
                leftIcon={<Save />}
              >
                {editingTemplate ? 'Zapisz zmiany' : 'Utwórz szablon'}
              </Button>
              <Button
                onClick={() => setShowTemplateForm(false)}
                variant="ghost"
              >
                Anuluj
              </Button>
            </div>
          </div>
        )}

        {/* Templates list */}
        {templates.length === 0 && !showTemplateForm ? (
          <EmptyState
            icon={FileText}
            title="Brak szablonów wiadomości"
            description="Utwórz szablon, aby szybciej odpowiadać na wiadomości"
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {visibleTemplates.length === 0 && !showTemplateForm && (
              <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-text-muted">
                W tej kategorii nie ma jeszcze szablonów.
              </div>
            )}
            {visibleTemplates.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-3 p-3 bg-surface-raised/30 rounded-xl border border-border-subtle hover:border-border transition-all"
              >
                <FileText className="w-4 h-4 text-text-muted flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary truncate">{t.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {t.store ? (
                      <span className="text-xxs text-text-muted flex items-center gap-1">
                        <Store className="w-3 h-3" /> {t.store.name}
                      </span>
                    ) : (
                      <span className="text-xxs text-accent-primary">Globalny</span>
                    )}
                    {t.category && (
                      <span className="rounded-full bg-accent-primary/10 px-1.5 py-0.5 text-xxs text-accent-primary">
                        {t.category.name}
                      </span>
                    )}
                    {!t.isActive && (
                      <span className="text-xxs text-warning">Nieaktywny</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditTemplate(t)}
                    className="btn-ghost p-1.5"
                    title="Edytuj"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => deleteTemplate(t.id)}
                    className="btn-ghost p-1.5 text-danger"
                    title="Usuń"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Dialog: wczytaj systemowe szablony */}
      <ConfirmDialog
        isOpen={showLoadSystemDialog}
        title="Szablony systemowe EcomFlow"
        subtitle={null}
        message={
          loadingSystemCount ? (
            <span className="text-text-secondary">Sprawdzam dostępne szablony…</span>
          ) : systemTemplatesCount === 0 ? (
            <span>Aktualnie nie ma żadnych systemowych szablonów do wczytania.</span>
          ) : (
            <span>
              Wgramy <strong>{systemTemplatesCount}</strong>{' '}
              {systemTemplatesCount === 1
                ? 'gotowy szablon'
                : systemTemplatesCount && systemTemplatesCount % 10 >= 2 && systemTemplatesCount % 10 <= 4 && (systemTemplatesCount % 100 < 10 || systemTemplatesCount % 100 >= 20)
                  ? 'gotowe szablony'
                  : 'gotowych szablonów'}{' '}
              z biblioteki EcomFlow do Twoich szablonów wiadomości.
            </span>
          )
        }
        description={
          systemTemplatesCount && systemTemplatesCount > 0 ? (
            <div className="space-y-2 text-sm leading-relaxed">
              <p>
                To gotowe odpowiedzi przygotowane przez zespół EcomFlow na podstawie najlepszych
                praktyk obsługi klienta - reklamacje, zwroty, zapytania o status, nieodebrane
                paczki itp.
              </p>
              <p>
                <strong className="text-text-primary">Po wczytaniu są w pełni Twoje.</strong>{' '}
                Edytujesz, usuwasz, zmieniasz treść - wszystko zostaje tylko na Twoim koncie.
                Nie musisz korzystać z całej biblioteki - możesz zostawić tylko te które Ci
                pasują, a resztę usunąć.
              </p>
              <p>
                <strong className="text-text-primary">Aktualizacje nie nadpiszą Twoich zmian.</strong>{' '}
                Jeśli zespół EcomFlow zaktualizuje szablon, a chcesz pobrać świeższą wersję -
                usuń u siebie i kliknij ten przycisk ponownie. Szablony o tej samej nazwie
                co już istniejące zostaną pominięte.
              </p>
            </div>
          ) : null
        }
        confirmLabel={systemTemplatesCount === 0 ? 'OK' : 'Wgraj szablony'}
        cancelLabel="Anuluj"
        danger={false}
        isPending={importingSystem}
        onConfirm={() => {
          if (systemTemplatesCount === 0) {
            setShowLoadSystemDialog(false);
            return;
          }
          confirmLoadSystem();
        }}
        onClose={() => {
          if (!importingSystem) setShowLoadSystemDialog(false);
        }}
      />

      <ConfirmDialog
        isOpen={Boolean(categoryToDelete)}
        title="Usunąć kategorię szablonów?"
        message={categoryToDelete
          ? `Kategoria „${categoryToDelete.name}” zostanie usunięta. Szablony pozostaną i trafią do sekcji „Bez kategorii”.`
          : ''}
        confirmLabel="Usuń kategorię"
        onConfirm={deleteTemplateCategory}
        onClose={() => setCategoryToDelete(null)}
      />

      {/* Sekcja: Etykiety */}
      {subpage === 'content' && (
      <div className="card p-5 space-y-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/20 to-success/20 border border-accent-primary/30 flex items-center justify-center">
              <Tag className="w-5 h-5 text-accent-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Etykiety</h2>
              <p className="text-xs text-text-secondary">Oznaczaj wiadomości kolorowymi etykietami</p>
            </div>
          </div>
          <Button onClick={openNewLabel} leftIcon={<Plus />}>
            Nowa etykieta
          </Button>
        </div>

        {showLabelForm && (
          <div className="bg-surface-raised/60 rounded-xl border border-border p-4 space-y-3 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-text-primary">
                {editingLabel ? 'Edytuj etykietę' : 'Nowa etykieta'}
              </h3>
              <button onClick={() => setShowLabelForm(false)} className="btn-ghost p-1">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-text-secondary mb-1 block">Nazwa</label>
                <Input
                  type="text"
                  value={labelForm.name}
                  onChange={e => setLabelForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="np. Ważne"
                />
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Kolor</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={labelForm.color}
                    onChange={e => setLabelForm(prev => ({ ...prev, color: e.target.value }))}
                    className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent"
                  />
                  <span className="text-xs text-text-muted">{labelForm.color}</span>
                </div>
              </div>
              <Button onClick={saveLabel} leftIcon={<Save />} className="h-10">
                {editingLabel ? 'Zapisz' : 'Utwórz'}
              </Button>
            </div>
          </div>
        )}

        {labelsList.length === 0 && !showLabelForm ? (
          <EmptyState
            icon={Tag}
            title="Brak etykiet"
            description="Utwórz etykietę, aby kategoryzować wiadomości"
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {labelsList.map(label => (
              <div
                key={label.id}
                className="flex items-center gap-3 p-3 bg-surface-raised/30 rounded-xl border border-border-subtle hover:border-border transition-all"
              >
                <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: label.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">{label.name}</p>
                  {label._count?.messages != null && (
                    <span className="text-xxs text-text-muted">{label._count.messages} wiadomości</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditLabel(label)} className="btn-ghost p-1.5" title="Edytuj">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteLabel(label.id)} className="btn-ghost p-1.5 text-danger" title="Usuń">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Sekcja: Reguły email */}
      {subpage === 'automation' && (
      <div className="card p-5 space-y-4 animate-slide-up" style={{ animationDelay: '0.5s' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning-bg to-warning-bg border border-warning-border flex items-center justify-center">
              <Zap className="w-5 h-5 text-warning" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Reguły email</h2>
              <p className="text-xs text-text-secondary">Automatycznie przetwarzaj przychodzące wiadomości</p>
            </div>
          </div>
          <Button onClick={openNewRule} leftIcon={<Plus />}>
            Nowa reguła
          </Button>
        </div>

        {/* Rule form */}
        {showRuleForm && (
          <div className="bg-surface-raised/60 rounded-xl border border-border p-4 space-y-4 animate-fade-in">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-text-primary">
                {editingRule ? 'Edytuj regułę' : 'Nowa reguła'}
              </h3>
              <button onClick={() => setShowRuleForm(false)} className="btn-ghost p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Nazwa reguły</label>
                <Input
                  type="text"
                  value={ruleForm.name}
                  onChange={e => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="np. InPost - oznacz jako przeczytane"
                />
              </div>
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Skrzynka pocztowa</label>
                <Select
                  value={ruleForm.storeId || ''}
                  onChange={e => setRuleForm(prev => ({ ...prev, storeId: e.target.value || null }))}
                >
                  <option value="">Wszystkie skrzynki</option>
                  {imapConfigs.map(cfg => (
                    <option key={cfg.id} value={cfg.store?.id || cfg.id}>
                      {cfg.name || cfg.fromEmail} {cfg.store ? `(${cfg.store.name})` : ''}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* Conditions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-text-secondary font-medium">Warunki:</label>
                  <div className="inline-flex rounded-lg border border-border overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setRuleForm(prev => ({ ...prev, conditionLogic: 'all' }))}
                      className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                        ruleForm.conditionLogic === 'all'
                          ? 'bg-accent-primary/20 text-accent-primary'
                          : 'text-text-muted hover:text-text-secondary'
                      }`}
                    >
                      Wszystkie
                    </button>
                    <button
                      type="button"
                      onClick={() => setRuleForm(prev => ({ ...prev, conditionLogic: 'any' }))}
                      className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                        ruleForm.conditionLogic === 'any'
                          ? 'bg-accent-primary/20 text-accent-primary'
                          : 'text-text-muted hover:text-text-secondary'
                      }`}
                    >
                      Dowolny
                    </button>
                  </div>
                  <span className="text-xs text-text-muted">
                    {ruleForm.conditionLogic === 'all' ? 'muszą być spełnione' : 'musi być spełniony'}
                  </span>
                </div>
                <button onClick={addCondition} className="text-xs text-accent-primary hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Dodaj warunek
                </button>
              </div>
              <div className="space-y-2">
                {ruleForm.conditions.map((cond, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-shrink-0" style={{ width: '160px' }}>
                      <Select
                        value={cond.field}
                        onChange={e => updateCondition(idx, 'field', e.target.value)}
                      >
                        {conditionFieldOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="flex-shrink-0" style={{ width: '160px' }}>
                      <Select
                        value={cond.operator}
                        onChange={e => updateCondition(idx, 'operator', e.target.value)}
                      >
                        {cond.field === 'has_attachments' ? (
                          <>
                            <option value="equals">tak</option>
                            <option value="not_equals">nie</option>
                          </>
                        ) : (
                          conditionOperatorOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))
                        )}
                      </Select>
                    </div>
                    {cond.field === 'has_attachments' ? (
                      <input type="hidden" value="true" />
                    ) : (
                      <Input
                        type="text"
                        value={cond.value}
                        onChange={e => updateCondition(idx, 'value', e.target.value)}
                        className="flex-1"
                        placeholder="wartość"
                      />
                    )}
                    {ruleForm.conditions.length > 1 && (
                      <button onClick={() => removeCondition(idx)} className="p-1 text-danger hover:text-danger">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-text-secondary font-medium">Akcje do wykonania</label>
                <button onClick={addAction} className="text-xs text-accent-primary hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Dodaj akcję
                </button>
              </div>
              <div className="space-y-2">
                {ruleForm.actions.map((action, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="flex-shrink-0" style={{ width: '200px' }}>
                      <Select
                        value={action.type}
                        onChange={e => updateAction(idx, 'type', e.target.value)}
                      >
                        {actionTypeOptions.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Select>
                    </div>
                    {action.type === 'add_label' && (
                      <Select
                        value={action.config.labelId || ''}
                        onChange={e => updateAction(idx, 'labelId', e.target.value)}
                        className="flex-1"
                      >
                        <option value="">Wybierz etykietę...</option>
                        {labelsList.map(l => (
                          <option key={l.id} value={l.id}>{l.name}</option>
                        ))}
                      </Select>
                    )}
                    {ruleForm.actions.length > 1 && (
                      <button onClick={() => removeAction(idx)} className="p-1 text-danger hover:text-danger">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={ruleForm.isActive}
                  onChange={e => setRuleForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                Aktywna
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={ruleForm.stopProcessing}
                  onChange={e => setRuleForm(prev => ({ ...prev, stopProcessing: e.target.checked }))}
                  className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
                />
                Zatrzymaj dalsze przetwarzanie
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={saveRule} leftIcon={<Save />}>
                {editingRule ? 'Zapisz zmiany' : 'Utwórz regułę'}
              </Button>
              <Button onClick={() => setShowRuleForm(false)} variant="ghost">
                Anuluj
              </Button>
            </div>
          </div>
        )}

        {/* Rules list */}
        {rulesList.length === 0 && !showRuleForm ? (
          <EmptyState
            icon={Zap}
            title="Brak reguł email"
            description='Utwórz regułę, aby automatycznie przetwarzać przychodzące wiadomości. Przykład: wiadomości od InPost → przenieś do folderu "InPost" + oznacz jako przeczytane'
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {rulesList.map((rule, idx) => (
              <div
                key={rule.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  rule.isActive
                    ? 'bg-surface-raised/30 border-border-subtle hover:border-border'
                    : 'bg-surface-raised/20 border-border-subtle opacity-60'
                }`}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveRulePosition(rule, 'up')}
                    disabled={idx === 0}
                    className="p-0.5 text-text-muted hover:text-text-primary disabled:opacity-20"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveRulePosition(rule, 'down')}
                    disabled={idx === rulesList.length - 1}
                    className="p-0.5 text-text-muted hover:text-text-primary disabled:opacity-20"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <Zap className={`w-4 h-4 flex-shrink-0 ${rule.isActive ? 'text-warning' : 'text-text-disabled'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-text-primary">{rule.name}</p>
                    {rule.store ? (
                      <span className="text-xxs px-1.5 py-0.5 rounded bg-accent-primary/10 text-accent-primary border border-accent-primary/20">
                        {rule.store.name}
                      </span>
                    ) : (
                      <span className="text-xxs px-1.5 py-0.5 rounded bg-surface-muted text-text-muted border border-border-subtle">
                        Wszystkie
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xxs text-text-muted">
                      {rule.conditions.length} warunek{rule.conditions.length !== 1 ? 'i' : ''}
                    </span>
                    <span className="text-xxs text-text-disabled">→</span>
                    <span className="text-xxs text-text-muted">
                      {rule.actions.length} akcj{rule.actions.length === 1 ? 'a' : rule.actions.length < 5 ? 'e' : 'i'}
                    </span>
                    {rule.matchCount > 0 && (
                      <span className="text-xxs text-accent-primary">
                        {rule.matchCount}x dopasowań
                      </span>
                    )}
                    {rule.stopProcessing && (
                      <span className="text-xxs text-warning">STOP</span>
                    )}
                    {!rule.isActive && (
                      <span className="text-xxs text-warning">Wyłączona</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleRuleActive(rule)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      rule.isActive ? 'text-success hover:bg-success/10' : 'text-text-muted hover:bg-surface-hover/50'
                    }`}
                    title={rule.isActive ? 'Wyłącz' : 'Włącz'}
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => applyRuleToExisting(rule)}
                    disabled={applyingRuleId === rule.id}
                    className="p-1.5 rounded-lg transition-colors text-accent-primary hover:bg-accent-primary/10 disabled:opacity-50"
                    title="Zastosuj do istniejących maili"
                  >
                    {applyingRuleId === rule.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => openEditRule(rule)} className="btn-ghost p-1.5" title="Edytuj">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteRule(rule.id)} className="btn-ghost p-1.5 text-danger" title="Usuń">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Statusy konwersacji (mapowanie status → folder IMAP) — tab 'general' */}
      {subpage === 'general' && imapConfigs.length > 0 && (
        <SettingsSection
          icon={GitBranch}
          title="Statusy konwersacji"
          subtitle="Mapowanie jest opcjonalne. Foldery są wykrywane cyklicznie co 10 minut. Po utworzeniu folderu w poczcie użyj Odśwież foldery, aby zobaczyć go od razu."
          actions={(
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={refreshImapFolders}
              disabled={refreshingImapFolders}
              leftIcon={<RefreshCw className={refreshingImapFolders ? 'animate-spin' : ''} />}
            >
              Odśwież foldery
            </Button>
          )}
        >
          <div className="space-y-3">
            {imapConfigs.map(cfg => (
              <div key={cfg.id} className="bg-surface-base/50 rounded-lg border border-border-subtle p-4">
                <p className="text-xs font-medium text-text-secondary mb-3">{cfg.fromEmail || cfg.name}</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-xs text-text-muted mb-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-success" /> Rozwiązane</label>
                    <Select
                      value={statusMapping.resolved[cfg.id] || ''}
                      onChange={e => setStatusMapping(prev => ({
                        ...prev,
                        resolved: { ...prev.resolved, [cfg.id]: e.target.value },
                      }))}
                    >
                      <option value="">- brak (pozostaje w Odebranych) -</option>
                      {(imapFolderOptions[cfg.id] || []).map(f => (
                        <option key={f.id} value={f.path}>{f.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-text-muted mb-1 flex items-center gap-1"><Star className="w-3 h-3 text-warning" /> Wyróżnione</label>
                    <Select
                      value={statusMapping.starred[cfg.id] || ''}
                      onChange={e => setStatusMapping(prev => ({
                        ...prev,
                        starred: { ...prev.starred, [cfg.id]: e.target.value },
                      }))}
                    >
                      <option value="">- brak (pozostaje w Odebranych) -</option>
                      {(imapFolderOptions[cfg.id] || []).map(f => (
                        <option key={f.id} value={f.path}>{f.name}</option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}

      {/* Folder na wysłane drafty AI (tab 'ai') — osobne ustawienie od statusFolderMapping.
          User wybiera JEDEN folder na skrzynkę gdzie trafiają maile po "Zatwierdź i wyślij",
          bez konieczności mapowania wszystkich statusów konwersacji. */}
      {subpage === 'ai' && imapConfigs.length > 0 && autoReplyConfig && (
        <SettingsSection
          icon={Bot}
          title="Folder dla wysłanych draftów AI"
          subtitle={'Po kliknięciu „Zatwierdź i wyślij" mail zostanie przeniesiony do wybranego folderu. Działa niezależnie od mapowania statusów konwersacji - wystarczy wybrać folder i gotowe.'}
          className="mt-4"
        >
          <div className="space-y-2">
            {imapConfigs.map(cfg => (
              <div key={cfg.id} className="flex items-center gap-3 p-3 rounded-lg bg-surface-raised/40 border border-border-subtle">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-text-primary truncate">{cfg.name || cfg.fromEmail}</div>
                  <div className="text-xs text-text-muted truncate">{cfg.fromEmail}</div>
                </div>
                <Select
                  value={autoReplyConfig.draftsTargetFolder?.[cfg.id] || ''}
                  onChange={e => {
                    const value = e.target.value;
                    setAutoReplyConfig(prev => prev ? {
                      ...prev,
                      draftsTargetFolder: {
                        ...prev.draftsTargetFolder,
                        ...(value ? { [cfg.id]: value } : {}),
                        ...(value ? {} : { [cfg.id]: '' }),
                      },
                    } : prev);
                  }}
                  className="flex-shrink-0 w-64"
                >
                  <option value="">- brak (użyj mapowania statusów) -</option>
                  {(imapFolderOptions[cfg.id] || []).map(f => (
                    <option key={f.id} value={f.path}>{f.name}</option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        </SettingsSection>
      )}

      {/* AI per skrzynka — toggle on/off + data aktywacji (tab 'ai') */}
      {subpage === 'ai' && imapConfigs.length > 0 && (
        <SettingsSection
          icon={Sparkles}
          title="AI kategoryzacja per skrzynka"
          subtitle={'Dodatkowa bramka do globalnego poziomu AI powyżej. Wyłączenie tutaj pomija kategoryzację i drafty dla wybranej skrzynki (np. adres "biuro@" gdzie nie chcesz automatyki).'}
          className="mt-4"
        >
          <div className="space-y-2">
            {imapConfigs.map((cfg) => {
              const aiOn = Boolean(cfg.aiEnabledFrom);
              const isSaving = savingAiConfig === cfg.id;
              return (
                <div
                  key={cfg.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-surface-raised/40 border border-border-subtle"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-1.5 rounded flex-shrink-0 ${aiOn ? 'bg-accent-primary/10' : 'bg-surface-hover'}`}>
                      <Sparkles className={`w-3.5 h-3.5 ${aiOn ? 'text-accent-primary' : 'text-text-disabled'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-text-primary truncate">{cfg.name || cfg.fromEmail}</div>
                      <div className="text-xs text-text-muted truncate">
                        {cfg.fromEmail}
                        {aiOn && cfg.aiEnabledFrom && (
                          <span className="text-text-secondary"> • AI od {new Date(cfg.aiEnabledFrom).toLocaleString('pl-PL')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleConfigAi(cfg)}
                    disabled={isSaving}
                    className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      aiOn ? 'bg-accent-primary/70' : 'bg-surface-muted'
                    } ${isSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                    title={aiOn ? 'Kliknij aby wyłączyć AI dla tej skrzynki' : 'Kliknij aby włączyć AI (start od teraz)'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        aiOn ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </SettingsSection>
      )}

      {/* Domyślna skrzynka — operator chce wymuszony kontekst jednej skrzynki (tab 'accounts') */}
      {subpage === 'automation' && imapConfigs.length > 0 && (
        <SettingsSection
          icon={Mail}
          title="Domyślna skrzynka mailowa"
          subtitle={'Skrzynka, której wiadomości pokażą się od razu po otwarciu modułu. "Wszystkie sklepy" zostały wycofane, żeby nie mieszać wątków z różnych kont. Możesz ręcznie przełączać się w sidebarze, ale przy każdym wejściu aplikacja wróci do wybranej tu skrzynki.'}
          className="mt-4"
        >
          <div className="flex items-center gap-3">
            <Select
              // Pod spodem zapisujemy defaultStoreId (storeId skrzynki). Każda
              // skrzynka IMAP jest powiązana z konkretnym sklepem, więc wybór
              // skrzynki jednoznacznie wskazuje sklep, którego listę mailową
              // wyświetlamy w module Wiadomości. Etykiety pokazują fromEmail,
              // żeby operator myślał w kategoriach skrzynek, nie sklepów.
              value={defaultStoreId ?? ''}
              onChange={(e) => setDefaultStoreId(e.target.value || null)}
              className="flex-1"
            >
              <option value="">- wybierz skrzynkę -</option>
              {imapConfigs
                .filter(cfg => cfg.store?.id)
                .map(cfg => (
                  <option key={cfg.id} value={cfg.store!.id}>
                    {cfg.fromEmail}
                    {cfg.name && cfg.name !== cfg.fromEmail ? ` (${cfg.name})` : ''}
                  </option>
                ))}
            </Select>
          </div>
        </SettingsSection>
      )}

      {/* Plakietki w menu bocznym (tab 'general') */}
      {subpage === 'general' && (
        <SettingsSection
          icon={Eye}
          title="Liczniki nieprzeczytanych wiadomości"
          subtitle="Wybierz, przy których grupach folderów ma pojawiać się liczba nieprzeczytanych wiadomości. Odznaczenie wyłącza plakietki dla całej grupy."
          className="mt-4"
          flushContent
        >
          <ToggleRow
            label="Licznik przy Wiadomościach w górnym menu"
            description="Pokazuje nieprzeczytane wiadomości z folderu Odebrane we wszystkich sklepach. Rozwiązane rozmowy nie są wliczane."
            checked={badgeDisplay.navigation}
            onChange={(next) => setBadgeDisplay(current => ({ ...current, navigation: next }))}
          />
          <ToggleRow
            label="Odebrane"
            description={'Plakietka przy głównym folderze "Odebrane" i per-kontowych inboxach (gdy masz więcej niż jedno konto).'}
            checked={badgeDisplay.inbox}
            onChange={(next) => setBadgeDisplay(current => ({ ...current, inbox: next }))}
          />
          <ToggleRow
            label="Foldery serwerowe"
            description={'Plakietki przy Wysłane / Szkice / Archiwum / Kosz / Spam oraz w drzewie "Wszystkie foldery" (własne foldery z serwera IMAP).'}
            checked={badgeDisplay.imapFolders}
            onChange={(next) => setBadgeDisplay(current => ({ ...current, imapFolders: next }))}
          />
          <ToggleRow
            label="Ulubione"
            description={'Plakietki przy folderach przypiętych do sekcji "Ulubione" w sidebarze.'}
            checked={badgeDisplay.favorites}
            onChange={(next) => setBadgeDisplay(current => ({ ...current, favorites: next }))}
          />
        </SettingsSection>
      )}

      {/* Gesty swipe na liście wiadomości (tab 'general') */}
      {subpage === 'general' && (() => {
        // Jedynym statusem konwersacji widocznym w skrzynce jest Rozwiązane.
        // Pozostałe akcje są bezpośrednimi operacjami na folderach IMAP.
        const actionOptions: Array<{ value: InboxSwipeAction; label: string; disabled: boolean; hint?: string }> = [
          { value: 'none', label: 'Brak akcji', disabled: false },
          { value: 'resolved', label: 'Rozwiązane', disabled: false },
          { value: 'drafts', label: 'Szkice', disabled: false },
          { value: 'archive', label: 'Archiwum', disabled: false },
          { value: 'trash', label: 'Kosz', disabled: false },
        ];
        return (
          <SettingsSection
            icon={GitBranch}
            title="Gesty swipe na liście"
            subtitle="Szybkie akcje na wiadomości przez przeciągnięcie palcem lub kursorem w poziomie. Działa też w przeglądarce (drag poziomy na wierszu wiadomości)."
            className="mt-4"
          >
            <div className="-mx-6 -mb-2">
              <ToggleRow
                label="Włącz gesty swipe"
                description="Domyślnie wyłączone. Po włączeniu wybierzesz akcje dla obu kierunków."
                checked={swipeEnabled}
                onChange={setSwipeEnabled}
              />
            </div>
            {swipeEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                <div className="p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowLeft className="w-3.5 h-3.5 text-success" />
                    <div className="text-xs font-semibold text-text-primary">Swipe w prawo (lewo → prawo)</div>
                  </div>
                  <div className="text-xxs text-text-muted mb-2">Podczas gestu po lewej stronie wiersza widać zieloną plakietkę.</div>
                  <Select
                    value={swipeRightAction}
                    onChange={(e) => setSwipeRightAction(e.target.value as InboxSwipeAction)}
                  >
                    {actionOptions.map(o => (
                      <option key={o.value} value={o.value} disabled={o.disabled} title={o.disabled ? o.hint : undefined}>
                        {o.label}{o.disabled ? ' (brak mappingu)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="p-3 rounded-lg bg-surface-base/50 border border-border-subtle">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowUp className="w-3.5 h-3.5 text-danger rotate-90" />
                    <div className="text-xs font-semibold text-text-primary">Swipe w lewo (prawo → lewo)</div>
                  </div>
                  <div className="text-xxs text-text-muted mb-2">Podczas gestu po prawej stronie wiersza widać czerwoną plakietkę.</div>
                  <Select
                    value={swipeLeftAction}
                    onChange={(e) => setSwipeLeftAction(e.target.value as InboxSwipeAction)}
                  >
                    {actionOptions.map(o => (
                      <option key={o.value} value={o.value} disabled={o.disabled} title={o.disabled ? o.hint : undefined}>
                        {o.label}{o.disabled ? ' (brak mappingu)' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </SettingsSection>
        );
      })()}

      {/* Stopka per skrzynka (tab 'content') */}
      {subpage === 'content' && imapConfigs.length > 0 && (
        <SettingsSection
          icon={FileText}
          title="Stopka mailowa (per skrzynka)"
          subtitle={'Stopka doklejana automatycznie do każdej odpowiedzi wychodzącej z danej skrzynki. Każda skrzynka może mieć własną. Separator "-- " (RFC 3676) jest dodawany automatycznie, nie musisz go wpisywać.'}
          className="mt-4"
        >
          <div className="space-y-4">
            {imapConfigs.map((cfg) => {
              const draft = signatureDrafts[cfg.id] || { html: '', text: '', marginTop: 10 };
              return (
                <div key={cfg.id} className="bg-surface-base/50 rounded-lg border border-border-subtle p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs font-medium text-text-secondary truncate">{cfg.name || cfg.fromEmail}</span>
                      <span className="text-xxs text-text-disabled truncate">{cfg.fromEmail}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">HTML (maile w formacie HTML)</label>
                      <textarea
                        rows={4}
                        value={draft.html}
                        onChange={(e) => setSignatureDrafts(prev => ({
                          ...prev,
                          [cfg.id]: { ...draft, html: e.target.value },
                        }))}
                        placeholder="<p><strong>Jan Kowalski</strong><br/>Sklep - kontakt@sklep.pl</p>"
                        className="w-full bg-surface-raised border border-border rounded-lg px-2 py-1.5 text-xs text-text-primary font-mono placeholder:text-text-muted focus:border-info-border focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted mb-1 block">Plain text (fallback dla klientów bez HTML)</label>
                      <textarea
                        rows={4}
                        value={draft.text}
                        onChange={(e) => setSignatureDrafts(prev => ({
                          ...prev,
                          [cfg.id]: { ...draft, text: e.target.value },
                        }))}
                        placeholder="Jan Kowalski&#10;Sklep - kontakt@sklep.pl"
                        className="w-full bg-surface-raised border border-border rounded-lg px-2 py-1.5 text-xs text-text-primary font-mono placeholder:text-text-muted focus:border-info-border focus:outline-none resize-none"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs text-text-muted mb-1 flex items-center justify-between">
                      <span>Margines nad stopką (px)</span>
                      <span className="text-text-secondary font-mono">{draft.marginTop ?? 10}</span>
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={1}
                      value={draft.marginTop ?? 10}
                      onChange={(e) => setSignatureDrafts(prev => ({
                        ...prev,
                        [cfg.id]: { ...draft, marginTop: Number(e.target.value) },
                      }))}
                      className="w-full accent-neon-pink"
                    />
                    <div className="flex justify-between text-xxs text-text-disabled mt-0.5">
                      <span>0 (blisko)</span>
                      <span>10 (domyślnie)</span>
                      <span>50 (daleko)</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SettingsSection>
      )}

      {/* RODO — retencja treści maili (tab 'rodo') */}
      {subpage === 'rodo' && (
      <div className="card p-5 mt-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-primary/20 to-info/20 border border-accent-primary/30 flex items-center justify-center">
            <Shield className="w-4 h-4 text-accent-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Retencja treści maili (RODO)</h3>
            <p className="text-xxs text-text-muted">
              Po ilu dniach automatycznie usuwać zapisane body maili. 0 = bez limitu (nie zalecane). Metadane (temat, nadawca) zostają zawsze.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2">
          <input
            type="range"
            min={0}
            max={730}
            step={30}
            value={inboxRetentionDays}
            onChange={(e) => setInboxRetentionDays(parseInt(e.target.value, 10))}
            className="flex-1 accent-accent-primary"
          />
          <div className="w-28 text-right">
            <span className="text-sm font-semibold text-text-primary">
              {inboxRetentionDays === 0 ? 'bez limitu' : `${inboxRetentionDays} dni`}
            </span>
          </div>
        </div>
        <p className="text-xxs text-text-muted mt-2">
          Domyślnie 365 dni. Zmiany zostaną zapisane po kliknięciu &quot;Zapisz ustawienia&quot; u dołu strony. Cron retencji biegnie codziennie o 3:00 (Europe/Warsaw).
        </p>
      </div>

      )}

      {/* RODO — dziennik audytu purge (tab 'rodo') */}
      {subpage === 'rodo' && (
      <div className="card p-5 mt-4">
        <button
          onClick={togglePurgeLogs}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-danger-bg to-danger-bg border border-danger-border flex items-center justify-center">
              <Shield className="w-4 h-4 text-danger" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Dziennik RODO - historia purge treści</h3>
              <p className="text-xxs text-text-muted">
                Immutable audit log: kto i kiedy kasował body maili (scope: manual / config_deletion / retention_cron).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/api/settings/inbox/purge-logs?format=csv"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-accent-primary hover:underline"
            >
              Pobierz CSV
            </a>
            <span className="text-xs text-text-muted">{purgeLogsOpen ? '▲' : '▼'}</span>
          </div>
        </button>

        {purgeLogsOpen && (
          <div className="mt-4 border-t border-border-subtle pt-4">
            {purgeLogsLoading ? (
              <div className="flex items-center gap-2 text-xs text-text-secondary">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Ładowanie…
              </div>
            ) : purgeLogs.length === 0 ? (
              <p className="text-xs text-text-muted">Brak wpisów w dzienniku RODO dla tego tenanta.</p>
            ) : (
              <>
                <div className="text-xs text-text-secondary mb-2">
                  Pokazano {purgeLogs.length} z {purgeLogsTotal} wpisów (najnowsze pierwsze)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-text-muted border-b border-border-subtle">
                        <th className="pb-2 pr-3 font-medium">Data</th>
                        <th className="pb-2 pr-3 font-medium">Scope</th>
                        <th className="pb-2 pr-3 font-medium">Scope ID</th>
                        <th className="pb-2 pr-3 font-medium">User</th>
                        <th className="pb-2 pr-3 font-medium text-right">Body</th>
                        <th className="pb-2 pr-3 font-medium text-right">Snippet</th>
                        <th className="pb-2 pr-3 font-medium text-right">Older than</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purgeLogs.map((log) => (
                        <tr key={log.id} className="border-b border-border-subtle text-text-secondary">
                          <td className="py-2 pr-3">{new Date(log.createdAt).toLocaleString('pl-PL')}</td>
                          <td className="py-2 pr-3">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xxs ${
                              log.scope === 'retention_cron' ? 'bg-info-bg text-info' :
                              log.scope === 'manual_purge' ? 'bg-danger/10 text-danger' :
                              log.scope === 'config_deletion' ? 'bg-warning/10 text-warning' :
                              'bg-surface-hover/50 text-text-secondary'
                            }`}>{log.scope}</span>
                          </td>
                          <td className="py-2 pr-3 font-mono text-xxs text-text-muted">{log.scopeId ?? '-'}</td>
                          <td className="py-2 pr-3 font-mono text-xxs text-text-muted">{log.triggeredBy ?? 'system'}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{log.bodiesDeleted}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">{log.snippetsCleared}</td>
                          <td className="py-2 pr-3 text-right tabular-nums text-text-muted">{log.olderThanDays ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      )}

      {/* Jedna belka zapisu dla bieżącej zakładki ustawień. */}
      {['general', 'automation', 'content', 'ai', 'rodo'].includes(subpage) && (
        <SettingsSaveBar
          saving={saving || (subpage === 'ai' && savingAutoReply)}
          status={saveStatus}
          onSave={saveCurrentSettings}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-xl border shadow-lg text-sm ${
          toast.type === 'success'
            ? 'bg-success-bg border-success-border text-success'
            : 'bg-danger-bg border-danger-border text-danger'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
